import * as i0 from '@angular/core';
import { Injectable, Directive, Input, InjectionToken, Inject, forwardRef, SkipSelf, Optional, EventEmitter, Output, HostBinding, isDevMode, NgModule } from '@angular/core';
import { Subject, Subscription, of, merge, fromEvent, animationFrameScheduler, Observable } from 'rxjs';
import { filter, throttleTime, debounceTime, takeUntil, switchMap, map, startWith, distinctUntilChanged } from 'rxjs/operators';

/**
 * Add entry into set.
 *
 * @param set Array list
 * @param entry Entry to add
 * @returns Entry index added
 */
function addEntry(set, entry) {
    let entryIndex = set.indexOf(entry);
    if (entryIndex === -1) {
        entryIndex = set.length;
        set[entryIndex] = entry;
    }
    return entryIndex;
}
/**
 * Delete entry from set.
 *
 * @param set Array list
 * @param entry Entry to delete
 * @returns Entry index deleted
 */
function deleteEntry(set, entry) {
    const entryIndex = set.indexOf(entry);
    if (entryIndex !== -1) {
        set.splice(entryIndex, 1);
    }
    return entryIndex;
}

/**
 * Abstract sticky container controller.
 */
class NgxStickyBaseContainerController {
    constructor() {
        this.boundaries = [];
        this.containers = [];
        this.intersections = [];
        this.stickies = [];
        this._stickyComputations = {};
        this._stickySnaps = {};
        this._intersectionComputations = {};
        this._intersectionSnaps = {};
    }
    disableAllStickies() {
        this.disableStickies();
        for (const container of this.containers) {
            container.disableStickies();
        }
    }
    enableAllStickies() {
        this.enableStickies();
        for (const container of this.containers) {
            container.enableStickies();
        }
    }
    getStickedOffset(position, viewportTop) {
        const container = this.getContainer();
        const viewportHeight = this.getViewportHeight();
        const stickies = this.stickies.map(stickyController => stickyController.getSticky());
        const stickySnaps = this.stickyEngine._collectStickySnaps(container, stickies, position, viewportHeight);
        return this.stickyEngine.determineStickedOffset(container, stickySnaps, position, viewportTop);
    }
    fixViewportTop(viewportTop, optionsOrExtraOffsetTop) {
        const options = typeof optionsOrExtraOffsetTop === 'number' ? { extraOffsetTop: optionsOrExtraOffsetTop } : optionsOrExtraOffsetTop;
        const viewportHeight = this.getViewportHeight();
        const viewportTopOffsetless = viewportTop - (options?.extraOffsetTop || 0);
        let stickedOffsetTop = 0;
        if (!options?.excludeStickies) {
            const container = this.getContainer();
            const stickies = this.stickies.map(stickyController => stickyController.getSticky());
            const stickySnaps = this.stickyEngine._collectStickySnaps(container, stickies, 'top', viewportHeight);
            // determine offset top height for the given viewport top
            const initialViewportTop = viewportTopOffsetless;
            const initialStickedOffset = this.stickyEngine.determineStickedOffset(container, stickySnaps, 'top', initialViewportTop);
            // determine offset top height for the given viewport subtracted by the offset top height
            let lastViewportTop = initialViewportTop - initialStickedOffset;
            let lastStickedOffset = initialStickedOffset && this.stickyEngine.determineStickedOffset(container, stickySnaps, 'top', lastViewportTop);
            // if both calculated heights are equal means that subtracting offset top height from
            // the given viewport is enought
            if (lastStickedOffset === initialStickedOffset) {
                stickedOffsetTop = lastStickedOffset;
            }
            else {
                // else we have to scroll up pixel by pixel to compute the best viewport top position
                const initialStickedOffsetMinusViewportHeight = initialViewportTop - viewportHeight;
                for (lastViewportTop = initialViewportTop - 1; lastViewportTop >= initialStickedOffsetMinusViewportHeight; lastViewportTop--) {
                    lastStickedOffset = this.stickyEngine.determineStickedOffset(container, stickySnaps, 'top', lastViewportTop);
                    if (initialViewportTop - lastViewportTop - lastStickedOffset >= 0) {
                        stickedOffsetTop = initialViewportTop - lastViewportTop;
                        break;
                    }
                }
            }
        }
        let viewportTopFixed = viewportTopOffsetless - stickedOffsetTop;
        if (this.containerParent) {
            viewportTopFixed -= this.getContainer().top;
        }
        return viewportTopFixed;
    }
    registerContainer(containerController) {
        addEntry(this.containers, containerController);
    }
    registerBoundary(boundaryController) {
        addEntry(this.boundaries, boundaryController);
    }
    registerIntersection(intersectionController) {
        const intersectionIndex = addEntry(this.intersections, intersectionController);
        if (intersectionIndex !== -1) {
            this._intersectionSnaps[intersectionIndex] = null;
            this._intersectionComputations[intersectionIndex] = null;
        }
    }
    registerSticky(stickyController) {
        const stickyIndex = addEntry(this.stickies, stickyController);
        if (stickyIndex !== -1) {
            this._stickySnaps[stickyIndex] = null;
            this._stickyComputations[stickyIndex] = null;
        }
    }
    updateAllStickies(fastUpdate) {
        this.updateStickies(fastUpdate);
        for (const containerController of this.containers) {
            containerController.updateAllStickies(fastUpdate);
        }
    }
    updateStickies(fastUpdate) {
        // // avoid update when there is no stickies
        // if (!this.stickies.length) {
        //   return;
        // }
        // // force full update when container size change
        // if (this.getContainerHeight() !== this._stickySnapContainerHeight) {
        //   fastUpdate = false;
        // }
        // get container viewport top before any update
        const containerViewportTop = this.getViewportTop();
        if (!fastUpdate) {
            this.beforeRefresh(fastUpdate);
            for (const boundaryController of this.boundaries) {
                boundaryController.beforeRefresh(fastUpdate);
            }
            for (let stickyIndex = 0; stickyIndex < this.stickies.length; ++stickyIndex) {
                this._stickyComputations[stickyIndex] = null;
                this._stickySnaps[stickyIndex] = null;
                this.stickies[stickyIndex].beforeRefresh(fastUpdate);
            }
            for (let intersectionIndex = 0; intersectionIndex < this.intersections.length; ++intersectionIndex) {
                this._intersectionComputations[intersectionIndex] = null;
                this._intersectionSnaps[intersectionIndex] = null;
                this.intersections[intersectionIndex].beforeRefresh(fastUpdate);
            }
        }
        //   // legacy code
        //   const viewportHeight = this.getViewportHeight() - this.stickyOffsetTop - this.stickyOffsetBottom;
        //   const viewportTop = containerViewportTop + this.getContainer().top + this.stickyOffsetTop;
        const container = this.getContainer();
        const viewportHeight = this.getViewportHeight();
        // compute absolute viewport top
        const viewportTop = containerViewportTop + this.getContainer().top;
        let stickies;
        // refresh stickies
        for (let stickyIndex = 0; stickyIndex < this.stickies.length; ++stickyIndex) {
            const stickyController = this.stickies[stickyIndex];
            let stickyComputation = this._stickyComputations[stickyIndex];
            if (!stickyComputation || stickyComputation.viewportTop !== viewportTop) {
                let stickySnap = this._stickySnaps[stickyIndex];
                if (!stickySnap) {
                    if (!stickies) {
                        stickies = [];
                        for (const _stickyController of this.stickies) {
                            stickies.push(_stickyController.getSticky());
                        }
                    }
                    stickySnap = this.stickyEngine.snapSticky(container, stickies, stickies[stickyIndex], viewportHeight);
                    this._stickySnaps[stickyIndex] = stickySnap;
                }
                stickyComputation = this.stickyEngine.determineStickyState(stickySnap, viewportTop);
                this._stickyComputations[stickyIndex] = stickyComputation;
            }
            stickyController.refresh(stickyComputation);
        }
        // refresh intersections
        for (let intersectionIndex = 0; intersectionIndex < this.intersections.length; ++intersectionIndex) {
            const intersectionController = this.intersections[intersectionIndex];
            let intersectionComputation = this._intersectionComputations[intersectionIndex];
            if (!intersectionComputation || intersectionComputation.viewportTop !== viewportTop) {
                let intersectionSnap = this._intersectionSnaps[intersectionIndex];
                if (!intersectionSnap) {
                    if (!stickies) {
                        stickies = [];
                        for (const _stickyController of this.stickies) {
                            stickies.push(_stickyController.getSticky());
                        }
                    }
                    const intersection = intersectionController.getIntersection();
                    intersectionSnap = this.stickyEngine.snapIntersection(container, stickies, intersection, viewportHeight);
                    this._intersectionSnaps[intersectionIndex] = intersectionSnap;
                }
                intersectionComputation = this.stickyEngine.determineIntersectionState(intersectionSnap, viewportTop);
                this._intersectionComputations[intersectionIndex] = intersectionComputation;
            }
            intersectionController.refresh(intersectionComputation);
        }
        if (!fastUpdate) {
            // restore container viewport top after full update
            this.scrollToTop(containerViewportTop, { excludeStickies: true });
        }
    }
    unregisterContainer(container) {
        deleteEntry(this.containers, container);
    }
    unregisterBoundary(boundary) {
        deleteEntry(this.boundaries, boundary);
    }
    unregisterIntersection(intersectionController) {
        const intersectionIndex = deleteEntry(this.intersections, intersectionController);
        if (intersectionIndex !== -1) {
            this._intersectionSnaps[intersectionIndex] = null;
            this._intersectionComputations[intersectionIndex] = null;
        }
    }
    unregisterSticky(sticky) {
        const stickyIndex = deleteEntry(this.stickies, sticky);
        if (stickyIndex !== -1) {
            this._stickySnaps[stickyIndex] = null;
            this._stickyComputations[stickyIndex] = null;
        }
    }
}

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/** Coerces a data-bound value (typically a string) to a boolean. */
function coerceBooleanProperty(value) {
    return value != null && `${value}` !== 'false';
}
function coerceNumberProperty(value, fallbackValue = 0) {
    return _isNumberValue(value) ? Number(value) : fallbackValue;
}
/**
 * Whether the provided value is considered a number.
 * @docs-private
 */
function _isNumberValue(value) {
    // parseFloat(value) handles most of the cases we're interested in (it treats null, empty string,
    // and other non-number values as NaN, where Number just uses 0) but it considers the string
    // '123hello' to be a valid number. Therefore we also check if Number(value) is NaN.
    return !isNaN(parseFloat(value)) && !isNaN(Number(value));
}

/**
 * An InputSubject is an Observable that coerces values and emit when change is detected.
 */
class InputSubject extends Subject {
    constructor(defaultValue, coercion) {
        super();
        this.defaultValue = defaultValue;
        this.coercion = coercion;
        /** Emit value changes */
        this.change$ = new Subject();
        this._firstChange = true;
        this._value = defaultValue;
    }
    /**
     * Returns current value.
     */
    getValue() {
        return this._value;
    }
    /**
     * Emit next value.
     *
     * @param value Next value
     * @param options Options to skip coercion
     */
    next(value, options) {
        if (!options || !options.skipCoercion) {
            if (value === this._valueSetted) {
                return;
            }
            this._valueSetted = value;
            if (this.coercion) {
                value = this.coercion(value);
            }
        }
        if (value !== this._value) {
            const firstChange = this._firstChange;
            const previousValue = this._value;
            this._firstChange = false;
            this._value = value;
            this.change$.next({
                previousValue,
                currentValue: value,
                firstChange,
            });
            super.next(value);
        }
    }
}

/**
 * A ConfigSubject is an Observable that coerces key-values and emit when change is detected
 */
class ConfigSubject extends Subject {
    constructor(schema) {
        super();
        /** Emit key-values changes */
        this.changes$ = new Subject();
        this._pushChangesSubscription = new Subscription();
        this.inputs = {};
        this._config = {};
        this._configChanges = {};
        this._aliases = {};
        const inputKeys = Object.keys(schema);
        for (const inputKey of inputKeys) {
            const inputOptions = schema[inputKey];
            if (inputOptions.aliasKey) {
                this._aliases[inputOptions.aliasKey] = inputKey;
            }
            const input = new InputSubject(inputOptions.defaultValue, inputOptions.coercion);
            this._config[inputKey] = inputOptions.defaultValue;
            this.inputs[inputKey] = input;
            const pushChangeSubscription = input.change$.subscribe(inputChange => {
                this._configChanged = true;
                this._configChanges[inputKey] = inputChange;
                this._config = { ...this._config };
                this._config[inputKey] = inputChange.currentValue;
            });
            this._pushChangesSubscription.add(pushChangeSubscription);
        }
    }
    /**
     * Returns current config.
     */
    getValue() {
        return this._config;
    }
    /**
     * Returns key-value.
     *
     * @param inputKey Input key
     * @returns key-value
     */
    getKeyValue(inputKey) {
        return this._config[inputKey];
    }
    /**
     * Emit next config.
     *
     * @param partialConfig Partial next config
     * @param options Options to skip coercion
     */
    next(partialConfig, options) {
        const inputKeys = Object.keys(partialConfig);
        for (const key of inputKeys) {
            const inputKey = (this._aliases[key] || key);
            if (inputKey in this.inputs) {
                const inputValue = (partialConfig)[key];
                const inputSubject = this.inputs[inputKey];
                inputSubject.next(inputValue, options);
            }
        }
        if (this._configChanged) {
            const changes = { ...this._configChanges };
            this._configChanged = false;
            this._configChanges = {};
            this.changes$.next(changes);
            super.next(this._config);
        }
    }
    /**
     * Emit next key-value.
     *
     * @param inputKey Input key
     * @param value key-value
     * @param options Options to skip coercion
     */
    nextKeyValue(inputKey, value, options) {
        this.next({ [inputKey]: value }, options);
    }
    /**
     * Apply simple changes as like ngOnChange(changes: SimpleChanges) input.
     *
     * @param changes Simple changes
     */
    nextChanges(changes) {
        const changeKeys = Object.keys(changes);
        const config = {};
        for (const inputKey of changeKeys) {
            config[inputKey] = changes[inputKey].currentValue;
        }
        this.next(config);
    }
}

/**
 * Returns getter for document height.
 *
 * @param win Window reference
 * @returns Getter for document height.
 */
function getDocumentHeightFactory(win) {
    if (!win) {
        return () => 0;
    }
    const documentHeightGetters = [
        () => win.document.body.scrollHeight,
        () => win.document.documentElement.scrollHeight,
        () => win.document.body.offsetHeight,
        () => win.document.documentElement.offsetHeight,
        () => win.document.body.clientHeight,
        () => win.document.documentElement.clientHeight,
    ];
    let documentHeightGetter = documentHeightGetters[0];
    let documentHeight = 0;
    for (const _documentHeightGetter of documentHeightGetters) {
        const _documentHeight = _documentHeightGetter();
        if (_documentHeight > documentHeight) {
            documentHeightGetter = _documentHeightGetter;
            documentHeight = _documentHeight;
        }
    }
    return documentHeightGetter;
}
/**
 * Returns getter for document width.
 *
 * @param win Window reference
 * @returns Getter for document width.
 */
function getDocumentWidthFactory(win) {
    if (!win) {
        return () => 0;
    }
    const documentWidthGetters = [
        () => win.document.body.scrollWidth,
        () => win.document.documentElement.scrollWidth,
        () => win.document.body.offsetWidth,
        () => win.document.documentElement.offsetWidth,
        () => win.document.body.clientWidth,
        () => win.document.documentElement.clientWidth,
    ];
    let documentWidthGetter = documentWidthGetters[0];
    let documentWidth = 0;
    for (const _documentWidthGetter of documentWidthGetters) {
        const _documentWidth = _documentWidthGetter();
        if (_documentWidth > documentWidth) {
            documentWidthGetter = _documentWidthGetter;
            documentWidth = _documentWidth;
        }
    }
    return documentWidthGetter;
}
/**
 * Returns element absolute rect.
 *
 * @param element Element
 * @returns Element absolute rect
 */
function getElementAbsoluteRect(element) {
    const rect = {
        height: element.offsetHeight,
        width: element.offsetWidth,
        left: 0,
        top: 0,
    };
    let currentElement = element;
    do {
        rect.top += currentElement.offsetTop || 0;
        rect.left += currentElement.offsetLeft || 0;
        currentElement = currentElement.offsetParent;
    } while (currentElement);
    return rect;
}
/**
 * Returns element relative rect.
 *
 * @param win Window reference
 * @param element Element
 * @returns Element relative rect.
 */
function getElementRelativeRect(win, element) {
    const rect = {
        height: element.offsetHeight,
        width: element.offsetWidth,
        left: 0,
        top: 0,
    };
    let currentElement = element;
    let currentElementStyle;
    do {
        currentElementStyle = currentElement !== element ? win.getComputedStyle(currentElement) : {};
        if (currentElementStyle.position === 'relative') {
            break;
        }
        if (currentElementStyle.position !== 'absolute') {
            rect.top += currentElement.offsetTop || 0;
            rect.left += currentElement.offsetLeft || 0;
        }
        currentElement = currentElement.offsetParent;
    } while (currentElement);
    return rect;
}
/**
 * Get window viewport height.
 *
 * @param win Window reference
 * @returns Window viewport height
 */
function getWindowViewportHeight(win) {
    return win && win.innerHeight || 0;
}
/**
 * Get window scroll left position.
 *
 * @param win Window reference
 * @returns Window scroll left position
 */
function getWindowViewportLeft(win) {
    if (!win) {
        return 0;
    }
    // The top-left-corner of the viewport is determined by the scroll position of the document
    // body, normally just (scrollLeft, scrollTop). However, Chrome and Firefox disagree about
    // whether `document.body` or `document.documentElement` is the scrolled element, so reading
    // `scrollTop` and `scrollLeft` is inconsistent. However, using the bounding rect of
    // `document.documentElement` works consistently, where the `top` and `left` values will
    // equal negative the scroll position.
    const documentRect = win.document.documentElement.getBoundingClientRect();
    return -documentRect.left
        || win.document.body.scrollLeft
        || win.scrollX
        || win.document.documentElement.scrollLeft
        || 0;
}
/**
 * Get window scroll top position.
 *
 * @param win Window reference
 * @returns Window scroll top position
 */
function getWindowViewportTop(win) {
    if (!win) {
        return 0;
    }
    // The top-left-corner of the viewport is determined by the scroll position of the document
    // body, normally just (scrollLeft, scrollTop). However, Chrome and Firefox disagree about
    // whether `document.body` or `document.documentElement` is the scrolled element, so reading
    // `scrollTop` and `scrollLeft` is inconsistent. However, using the bounding rect of
    // `document.documentElement` works consistently, where the `top` and `left` values will
    // equal negative the scroll position.
    const documentRect = win.document.documentElement.getBoundingClientRect();
    return -documentRect.top
        || win.document.body.scrollTop
        || win.scrollY
        || win.document.documentElement.scrollTop
        || 0;
}
/**
 * Returns `true` when element is scrollable.
 *
 * @param win Window reference
 * @param element Element
 * @returns `true` when element is scrollable
 */
function isElementScrollableY(win, element) {
    return element.offsetHeight < element.scrollHeight && win.getComputedStyle(element).overflowY === 'auto';
}
/**
 * Set styles on a given element.
 *
 * @param renderer Renderer2 instance
 * @param element Element
 * @param styles Styles
 */
function setElementStyles(renderer, element, styles) {
    if (!element || !styles) {
        return;
    }
    const propKeys = Object.keys(styles);
    for (const prop of propKeys) {
        const value = styles[prop];
        if (value) {
            renderer.setStyle(element, prop, value);
        }
        else {
            renderer.removeStyle(element, prop);
        }
    }
}
function isSmoothScrollSupported(win) {
    return win
        && win.document
        && win.document.documentElement
        && win.document.documentElement.style
        && 'scrollBehavior' in win.document.documentElement.style;
}
/** @internal */
function scrollToFactory(win, container) {
    if (!win) {
        return () => { };
    }
    const smoothScrollSupported = isSmoothScrollSupported(win);
    const nativeScrollToFn = container ? container.scrollTo : win.scrollTo;
    const scrollThisArg = container ? container : win;
    const scrollTarget = container ? container : win.document.documentElement;
    const getViewportLeft = container ? () => container.scrollLeft : () => getWindowViewportLeft(win);
    const getViewportTop = container ? () => container.scrollTop : () => getWindowViewportTop(win);
    const parseOptions = (optionsOrX, mayY) => {
        return optionsOrX != null && typeof optionsOrX !== 'object'
            // scrollTo(x: number, y: number): void
            ? {
                left: !isNaN(+optionsOrX) ? +optionsOrX : getViewportLeft(),
                top: mayY != null && !isNaN(+mayY) ? +mayY : getViewportTop(),
            }
            // scrollTo(options: ScrollToOptions): void
            : optionsOrX != null && typeof optionsOrX === 'object' && ('top' in optionsOrX || 'left' in optionsOrX)
                ? {
                    ...optionsOrX,
                    left: optionsOrX.left != null && !isNaN(+optionsOrX.left) ? +optionsOrX.left : getViewportLeft(),
                    top: optionsOrX.top != null && !isNaN(+optionsOrX.top) ? +optionsOrX.top : getViewportTop(),
                }
                : null;
    };
    const scrollToFn = nativeScrollToFn
        ? smoothScrollSupported
            // function which calls native scrollTo with options as object
            ? options => {
                nativeScrollToFn.call(scrollThisArg, options);
            }
            // function which calls native scrollTo with flatten options because options as object is not supported
            : options => {
                nativeScrollToFn.call(scrollThisArg, options.left, options.top);
            }
        // function which polyfills native scrollTo
        : options => {
            if (options.left !== scrollTarget.scrollLeft) {
                scrollTarget.scrollLeft = options.left;
            }
            if (options.top !== scrollTarget.scrollTop) {
                scrollTarget.scrollTop = options.top;
            }
        };
    return ((optionsOrX, mayY) => {
        const options = parseOptions(optionsOrX, mayY);
        if (options) {
            scrollToFn(options);
        }
    });
}

function coerceStickyPosition(value) {
    return isStickyPositionBottom(value) ? 'bottom' : 'top';
}
function coerceStickyDirection(value) {
    return isStickyDirectionDown(value) ? 'down' : 'up';
}
function compareStickiesComputed(a, b) {
    return a.positionBottom === b.positionBottom
        ? a.sortPoint < b.sortPoint && a.boundary.top >= b.boundary.top ? 1 : -1
        : -1;
}
function getStuckedPositionTop(computation) {
    const { boundary, directionDown, height: elementHeight, positionBottom, } = computation.snap.stickyComputed;
    let result = directionDown
        ? boundary.top
            + boundary.height
            - elementHeight
            - (positionBottom
                ? computation.offsetStucked
                : boundary.offsetBottom - elementHeight - computation.offsetStucked)
        : positionBottom
            ? boundary.top + boundary.offsetTop - elementHeight - computation.offsetStucked
            : boundary.top + computation.offsetStucked;
    // adjust position when sticky has spot
    if (computation.snap.sticky.spot) {
        result = directionDown
            ? result
                + computation.offsetSticked + computation.offsetStucked
                + computation.oppositeOffsetSticked + computation.oppositeOffsetStucked
            : result
                - computation.offsetSticked - computation.offsetStucked
                - computation.oppositeOffsetSticked - computation.oppositeOffsetStucked;
    }
    return result;
}
function isStickyPositionBottom(position) {
    return position === 'bottom';
}
function isStickyDirectionDown(direction) {
    return direction !== 'up';
}

const NGX_STICKY_ENGINE_INTERCEPTION_STATE_MAP = {
    'sticked,normal': 'enter',
    'stucked,normal': 'entered',
    'sticked,sticked': 'entered',
    'stucked,sticked': 'exit',
    'stucked,stucked': 'exited',
};
/**
 * Defines a sticky engine. Implemented in universal way.
 */
class NgxStickyEngine {
    /**
     * Compute sticky boundary.
     *
     * @param container Container
     * @param boundary Sticky boundary
     * @param sticky Sticky line
     * @param directionDown Direction down
     * @param spot Spot line
     * @param viewportHeight Viewport height
     * @returns Boundary instance
     */
    computeStickyBoundary(container, boundary, sticky, directionDown, spot, viewportHeight) {
        let { height: boundaryHeight, top: boundaryTop, left: boundaryLeft, width: boundaryWidth, } = boundary || container;
        boundaryHeight = boundaryHeight || 0;
        boundaryTop = boundaryTop || 0;
        boundaryWidth = boundaryWidth || 0;
        boundaryLeft = boundaryLeft || 0;
        if (spot && spot.height) {
            const beforeSpot = sticky.top < spot.top;
            if (beforeSpot) {
                // when sticky direction is down and is before its spot
                if (directionDown) {
                    const spotPoint = spot.top - viewportHeight;
                    // disable sticky when is in same viewport height as its spot
                    if (spotPoint < sticky.top) {
                        boundaryHeight = 0;
                        boundaryTop = 0;
                        // adjust sticky boundary height according to its spot when spot point is in base boundary
                    }
                    else if (spotPoint < boundaryTop + boundaryHeight) {
                        boundaryHeight = spotPoint - boundaryTop;
                    }
                }
            }
            else {
                // when sticky direction is up and is after its spot
                if (!directionDown) {
                    const spotPoint = spot.top + spot.height + viewportHeight;
                    // disable sticky when is in same viewport height as its spot
                    if (spotPoint > sticky.top) {
                        boundaryHeight = 0;
                        boundaryTop = 0;
                        // adjust sticky boundary top according to its spot when spot point is in base boundary
                    }
                    else if (spotPoint > boundaryTop && spotPoint < boundaryTop + boundaryHeight) {
                        boundaryHeight -= spotPoint - boundaryTop;
                        boundaryTop = spotPoint;
                    }
                }
            }
        }
        return {
            height: boundaryHeight,
            top: boundaryTop,
            width: boundaryWidth,
            left: boundaryLeft,
            unstacked: boundary && boundary.unstacked || container.unstacked || false,
            offsetBottom: 0,
            offsetTop: 0,
        };
    }
    /**
     * Compute sticky sticked line.
     *
     * @param boundary Sticky boundary line
     * @param sticky Sticky line
     * @param positionBottom Position bottom
     * @param directionDown Direction down
     * @param viewportHeight Viewport height
     * @returns Sticked line
     */
    computeStickyStickedLine(boundary, sticky, positionBottom, directionDown, viewportHeight) {
        let stickedTop;
        let stickedHeight;
        if (positionBottom) {
            if (directionDown) {
                stickedTop = sticky.top + sticky.height - viewportHeight;
                stickedHeight = boundary.height + boundary.top - stickedTop - viewportHeight;
            }
            else {
                stickedTop = boundary.top - viewportHeight;
                stickedHeight = sticky.top - stickedTop - viewportHeight;
            }
        }
        else {
            if (directionDown) {
                stickedTop = sticky.top;
                stickedHeight = boundary.height + boundary.top - stickedTop;
            }
            else {
                stickedTop = boundary.top;
                stickedHeight = sticky.top - stickedTop;
            }
        }
        return { top: stickedTop, height: stickedHeight };
    }
    /**
     * Compute sticky sort point.
     *
     * @param sticky Sticky line
     * @param positionBottom Position bottom
     * @param directionDown Direction down
     * @param viewportHeight Viewport height
     * @returns Sticky sort point
     */
    computeStickySortPoint(sticky, positionBottom, directionDown, viewportHeight) {
        let sortPoint;
        if (positionBottom) {
            sortPoint = directionDown
                ? -sticky.top - sticky.height + viewportHeight
                : sticky.top + sticky.height - viewportHeight;
        }
        else {
            sortPoint = directionDown
                ? -sticky.top
                : sticky.top;
        }
        return sortPoint;
    }
    /**
     * Determines intersection state.
     *
     * @param snap Intersection snap
     * @param viewportTop Viewport/scroll top position
     * @returns Intersection computation
     */
    determineIntersectionState(snap, viewportTop) {
        const enter = this.determineStickyState(snap.enter, viewportTop);
        const exit = this.determineStickyState(snap.exit, viewportTop);
        // Intersection state can be easily determined from enter sticky and exit sticky states:
        // - enter: when enter sticky (on bottom) is _sticked_ and exit sticky (on top) is _normal_ ;
        // - entered: when enter (on bottom) and exit (on top) stickies are _sticked_ ;
        // - entered: when enter sticky (on bottom) is _stucked_ and exit sticky (on top) is _normal_ ;
        // - exit: when enter sticky (on bottom) is _stucked_ and exit sticky (on top) is _sticked_ ;
        // - exited: when enter (on bottom) and exit (on top) stickies are _stucked_.
        const stateKey = [enter.state, exit.state].join(',');
        const state = NGX_STICKY_ENGINE_INTERCEPTION_STATE_MAP[stateKey] || null;
        const viewportOffsetless = snap.viewportHeight - enter.offsetSticked - exit.offsetSticked;
        const maxHeightVisible = Math.min(snap.intersection.height, viewportOffsetless);
        let height = 0;
        let intersecting = false;
        if (state === 'entered') {
            height = maxHeightVisible;
            intersecting = true;
        }
        else if (state === 'enter') {
            height = viewportTop + snap.viewportHeight - snap.intersection.top - enter.offsetSticked;
            intersecting = true;
        }
        else if (state === 'exit') {
            height = snap.intersection.top + snap.intersection.height - viewportTop - exit.offsetSticked;
            intersecting = true;
        }
        const computation = {
            enter,
            exit,
            snap,
            height,
            intersecting,
            ratio: Math.min(1, height / maxHeightVisible),
            state,
            viewportTop,
        };
        return computation;
    }
    /**
     * Determines sticky state.
     *
     * @param snap Sticky snap
     * @param viewportTop Viewport/scroll top position
     * @returns Sticky computation
     */
    determineStickyState(snap, viewportTop) {
        const computation = {
            offsetSticked: 0,
            offsetStucked: 0,
            oppositeOffsetSticked: 0,
            oppositeOffsetStucked: 0,
            oppositeState: 'normal',
            state: 'normal',
            snap,
            viewportTop,
        };
        // cancel computation when stickyComputed is outside its boundary
        if (snap.stickyComputed.disabled) {
            return computation;
        }
        // last value stored in _stickyComputedState will be related to opposite of stickyComputed
        let _oppositeStickyComputedState = 'normal';
        // last value stored in _stickyComputedState will be related to stickyComputed
        let _stickyComputedState = 'normal';
        // compute state for each opposite sibling
        for (const _oppositeStickyComputed of snap.oppositeStickies) {
            const boundaryOffset = _oppositeStickyComputed.directionDown
                ? _oppositeStickyComputed.boundary.offsetBottom
                : _oppositeStickyComputed.boundary.offsetTop;
            let { top: _oppositeStickedTop, height: _oppositeStickedHeight, } = _oppositeStickyComputed.sticked;
            // adjust _oppositeStickyComputed sticked line with previous sibling
            if (_oppositeStickyComputed.positionBottom) {
                if (_oppositeStickyComputed.directionDown) {
                    _oppositeStickedTop += computation.oppositeOffsetSticked + computation.oppositeOffsetStucked;
                    _oppositeStickedHeight -= computation.oppositeOffsetStucked;
                }
                else {
                    _oppositeStickedTop += boundaryOffset;
                    _oppositeStickedTop += computation.oppositeOffsetSticked;
                    _oppositeStickedHeight -= boundaryOffset - computation.oppositeOffsetStucked;
                    _oppositeStickedHeight += _oppositeStickyComputed.height;
                }
            }
            else {
                if (_oppositeStickyComputed.directionDown) {
                    _oppositeStickedTop -= computation.oppositeOffsetSticked + computation.oppositeOffsetStucked;
                    _oppositeStickedHeight += computation.oppositeOffsetStucked;
                    _oppositeStickedHeight -= boundaryOffset;
                }
                else {
                    _oppositeStickedTop -= computation.oppositeOffsetSticked;
                    _oppositeStickedHeight -= computation.oppositeOffsetStucked;
                }
            }
            // set default state to "normal"
            _oppositeStickyComputedState = 'normal';
            // determine _oppositeStickyComputed state with its sticked line adjusted
            // if (viewportTop > _oppositeStickedTop) {
            if (viewportTop >= _oppositeStickedTop) {
                _oppositeStickyComputedState = 'sticked';
                if (viewportTop > _oppositeStickedTop + _oppositeStickedHeight) {
                    _oppositeStickyComputedState = _oppositeStickyComputed.directionDown ? 'stucked' : 'normal';
                }
            }
            else if (!_oppositeStickyComputed.directionDown) {
                _oppositeStickyComputedState = 'stucked';
            }
            // cumulate opposite sibling height to the right offset
            if (
            // and state determined is "sticked" or "stucked"
            _oppositeStickyComputedState !== 'normal'
                // and _oppositeStickyComputed is stacked
                && !_oppositeStickyComputed.boundary.unstacked) {
                if (_oppositeStickyComputed.boundary.top === snap.stickyComputed.boundary.top
                    && _oppositeStickyComputed.boundary.height === snap.stickyComputed.boundary.height) {
                    computation.oppositeOffsetStucked += _oppositeStickyComputed.height;
                }
                else if (_oppositeStickyComputedState === 'sticked') {
                    computation.oppositeOffsetSticked += _oppositeStickyComputed.height;
                }
            }
        }
        computation.oppositeState = _oppositeStickyComputedState;
        // compute state for each sibling and stickyComputed in last
        for (const _stickyComputed of snap.stickies) {
            const boundaryOffset = _stickyComputed.directionDown
                ? _stickyComputed.boundary.offsetBottom
                : _stickyComputed.boundary.offsetTop;
            let { top: _stickedTop, height: _stickedHeight, } = _stickyComputed.sticked;
            // adjust _stickyComputed sticked line with previous sibling
            if (_stickyComputed.positionBottom) {
                if (_stickyComputed.directionDown) {
                    _stickedTop += computation.offsetSticked + computation.offsetStucked;
                    _stickedHeight -= computation.offsetStucked;
                }
                else {
                    _stickedTop += boundaryOffset;
                    _stickedTop += computation.offsetSticked;
                    _stickedHeight -= boundaryOffset - computation.offsetStucked;
                    _stickedHeight += _stickyComputed.height;
                }
            }
            else {
                if (_stickyComputed.directionDown) {
                    _stickedTop -= computation.offsetSticked + computation.offsetStucked;
                    _stickedHeight += computation.offsetStucked;
                    _stickedHeight -= boundaryOffset;
                }
                else {
                    _stickedTop -= computation.offsetSticked;
                    _stickedHeight -= computation.offsetStucked;
                }
            }
            // adjust the current _stickyComputed sticked line with previous and opposite siblings when sticky has spot
            if (snap.sticky.spot && _stickyComputed === snap.stickyComputed) {
                const sumOfCurrentAndOppositeOffsets = computation.offsetSticked + computation.offsetStucked
                    + computation.oppositeOffsetSticked + computation.oppositeOffsetStucked;
                // increase sticked line height with sum of sticked and stucked offsets from previous and opposite siblings
                _stickedHeight += sumOfCurrentAndOppositeOffsets;
                // subtract sum of sticked and stucked offsets from previous and opposite siblings when direction is up
                if (!_stickyComputed.directionDown) {
                    _stickedTop -= sumOfCurrentAndOppositeOffsets;
                }
            }
            // set default state to "normal"
            _stickyComputedState = 'normal';
            // determine _stickyComputed state with its sticked line adjusted
            // if (viewportTop > _stickedTop) {
            if (viewportTop >= _stickedTop) {
                _stickyComputedState = 'sticked';
                if (viewportTop > _stickedTop + _stickedHeight) {
                    _stickyComputedState = _stickyComputed.directionDown ? 'stucked' : 'normal';
                }
            }
            else if (!_stickyComputed.directionDown) {
                _stickyComputedState = 'stucked';
            }
            // cumulate sibling height to the right offset
            if (
            // when _stickyComputed isn't stickyComputed
            _stickyComputed !== snap.stickyComputed
                // and state determined is "sticked" or "stucked"
                && _stickyComputedState !== 'normal'
                // and _stickyComputed is stacked
                && !_stickyComputed.boundary.unstacked) {
                if (_stickyComputed.boundary.top === snap.stickyComputed.boundary.top
                    && _stickyComputed.boundary.height === snap.stickyComputed.boundary.height) {
                    computation.offsetStucked += _stickyComputed.height;
                }
                else if (_stickyComputedState === 'sticked') {
                    computation.offsetSticked += _stickyComputed.height;
                }
            }
        }
        computation.state = _stickyComputedState;
        return computation;
    }
    /**
     * Returns offset height used by sticked stickies for a given side and viewport position.
     *
     * @param container Container
     * @param stickySnaps Array of sticky snaps
     * @param position Position `"top"` or `"bottom"`
     * @param viewportTop Viewport/scroll top position
     */
    determineStickedOffset(container, stickySnaps, position, viewportTop) {
        const positionBottom = isStickyPositionBottom(position);
        const containerOffset = positionBottom ? container.offsetBottom || 0 : container.offsetTop || 0;
        let stickedOffset = 0;
        let heightOfStickyUnstackedWithMaxHeight = 0;
        for (const stickySnap of stickySnaps) {
            const { stickyComputed } = stickySnap;
            if (
            // skip sticky which have different container reference than the given
            stickySnap.container !== container
                // skip sticky which have different position than the given
                || stickyComputed.positionBottom !== positionBottom
                // skip sticky when is disabled
                || stickyComputed.disabled) {
                continue;
            }
            const computation = this.determineStickyState(stickySnap, viewportTop);
            // add sticky height to sticked offset when state is sticked
            if (computation.state === 'sticked') {
                // add height when sticky is stacked
                if (!stickyComputed.boundary.unstacked) {
                    stickedOffset += stickyComputed.height;
                    // or update the height of sticky unstacked with max height
                }
                else if (stickyComputed.height > heightOfStickyUnstackedWithMaxHeight) {
                    heightOfStickyUnstackedWithMaxHeight = stickyComputed.height;
                }
            }
        }
        return containerOffset + stickedOffset + heightOfStickyUnstackedWithMaxHeight;
    }
    /**
     * Create intersection snap.
     *
     * @param container Container
     * @param stickies Stickies
     * @param intersection Intersection
     * @param viewportHeight Viewport height
     * @returns Intersection snap
     */
    snapIntersection(container, stickies, intersection, viewportHeight) {
        const disabled = intersection.disabled;
        // enter sticky is sticked on bottom
        const enterSticky = {
            boundary: {
                top: intersection.top - 1,
                height: intersection.height,
                left: container.left,
                width: container.width,
            },
            disabled,
            top: intersection.top - 1,
            height: 1,
            position: 'bottom',
            direction: 'down',
        };
        // exit sticky is sticked on top
        const exitSticky = {
            boundary: {
                top: intersection.top,
                height: intersection.height - 1,
                left: container.left,
                width: container.width,
            },
            disabled,
            top: intersection.top,
            height: 1,
            position: 'top',
            direction: 'down',
        };
        return {
            container,
            enter: this.snapSticky(container, stickies, enterSticky, viewportHeight),
            exit: this.snapSticky(container, stickies, exitSticky, viewportHeight),
            intersection: {
                disabled,
                height: intersection.height,
                thresholds: intersection.thresholds ? [...intersection.thresholds] : [0, 1],
                top: intersection.top,
            },
            viewportHeight,
        };
    }
    /**
     * Create sticky snap.
     *
     * @param container Container
     * @param stickies Stickies
     * @param sticky Sticky
     * @param viewportHeight Viewport height
     * @returns Sticky snap
     */
    snapSticky(container, stickies, sticky, viewportHeight) {
        const boundariesMap = {};
        const directionDown = isStickyDirectionDown(sticky.direction);
        const positionBottom = isStickyPositionBottom(sticky.position);
        const stickyComputed = {
            boundary: this.computeStickyBoundary(container, sticky.boundary, sticky, directionDown, sticky.spot, viewportHeight),
            directionDown,
            disabled: false,
            height: sticky.height,
            positionBottom,
            sortPoint: this.computeStickySortPoint(sticky, positionBottom, directionDown, viewportHeight),
            sticked: null,
            top: sticky.top,
        };
        if (
        // disable when sticky's container is disabled
        container.disabled
            // disable when sticky is disabled
            || sticky.disabled
            // disable when sticky has zero height
            || !sticky.height
            // disable when sticky has height equal or greater than viewport height
            // || sticky.height >= viewportHeight
            // disable when sticky is outside its boundary
            || sticky.top < stickyComputed.boundary.top
            || sticky.top > stickyComputed.boundary.top + stickyComputed.boundary.height) {
            stickyComputed.disabled = true;
            stickyComputed.sticked = { height: 0, top: 0 };
        }
        else {
            stickyComputed.sticked = this.computeStickyStickedLine(stickyComputed.boundary, sticky, positionBottom, directionDown, viewportHeight);
        }
        const stickiesComputed = [];
        const oppositeStickiesComputed = [];
        let offsetSpacer;
        let oppositeOffsetSpacer;
        if (sticky.disabled) {
            return {
                boundaries: boundariesMap,
                container,
                oppositeStickies: oppositeStickiesComputed,
                stickies: stickiesComputed,
                sticky,
                stickyComputed,
                viewportHeight,
            };
        }
        // insert fake sticky which represent container offset top
        if (container.offsetTop) {
            if (!stickyComputed.positionBottom) {
                offsetSpacer = {
                    boundary: container,
                    direction: 'down',
                    height: container.offsetTop,
                    position: 'top',
                    disabled: false,
                    top: container.top,
                };
                stickies = [offsetSpacer, ...stickies];
            }
            else {
                oppositeOffsetSpacer = {
                    boundary: container,
                    direction: 'up',
                    height: container.offsetTop,
                    position: 'bottom',
                    disabled: false,
                    top: container.top + container.height - container.offsetTop,
                };
                stickies = [oppositeOffsetSpacer, ...stickies];
            }
        }
        // insert fake sticky which represent container offset bottom
        if (container.offsetBottom) {
            if (stickyComputed.positionBottom) {
                offsetSpacer = {
                    boundary: container,
                    direction: 'up',
                    height: container.offsetBottom,
                    position: 'bottom',
                    disabled: false,
                    top: container.top + container.height - container.offsetBottom,
                };
                stickies = [offsetSpacer, ...stickies];
            }
            else {
                oppositeOffsetSpacer = {
                    boundary: container,
                    direction: 'down',
                    height: container.offsetBottom,
                    position: 'top',
                    disabled: false,
                    top: container.top,
                };
                stickies = [oppositeOffsetSpacer, ...stickies];
            }
        }
        // remove 1px to fix round sizes (offsetLeft and offsetWidth)
        const stickyComputedBoundaryRight = stickyComputed.boundary.left + stickyComputed.boundary.width - 1;
        for (let _stickyIndex = 0; _stickyIndex < stickies.length; ++_stickyIndex) {
            const _sticky = stickies[_stickyIndex];
            let _directionDown;
            let _positionBottom;
            let _stickyComputed;
            let _stickyComputedBoundaryRight;
            if (_sticky === sticky) {
                _directionDown = stickyComputed.directionDown;
                _positionBottom = stickyComputed.positionBottom;
                _stickyComputed = stickyComputed;
                _stickyComputedBoundaryRight = stickyComputedBoundaryRight;
            }
            else {
                _directionDown = isStickyDirectionDown(_sticky.direction);
                _positionBottom = isStickyPositionBottom(_sticky.position);
                _stickyComputed = {
                    boundary: this.computeStickyBoundary(container, _sticky.boundary, _sticky, _directionDown, _sticky.spot, viewportHeight),
                    disabled: false,
                    directionDown: _directionDown,
                    height: _sticky.height,
                    positionBottom: _positionBottom,
                    sortPoint: this.computeStickySortPoint(_sticky, _positionBottom, _directionDown, viewportHeight),
                    sticked: null,
                    top: _sticky.top,
                };
                // remove 1px to fix round sizes (offsetLeft and offsetWidth)
                _stickyComputedBoundaryRight = _stickyComputed.boundary.left + _stickyComputed.boundary.width - 1;
                if (
                // skip sticky which is disabled
                _sticky.disabled
                    // skip sticky which has zero height
                    || !_sticky.height
                    // skip sticky which has height equal or greater than viewport height
                    // || _sticky.height >= viewportHeight
                    // skip sticky which isn't in its boundary
                    || _sticky.top < _stickyComputed.boundary.top
                    || _sticky.top > _stickyComputed.boundary.top + _stickyComputed.boundary.height
                    // skip sticky sibling when its boundary isn't align horizontaly
                    || stickyComputedBoundaryRight <= _stickyComputed.boundary.left
                    || stickyComputed.boundary.left >= _stickyComputedBoundaryRight) {
                    _stickyComputed.disabled = true;
                    _stickyComputed.sticked = { height: 0, top: 0 };
                }
                else {
                    _stickyComputed.sticked = this.computeStickyStickedLine(_stickyComputed.boundary, _sticky, _positionBottom, _directionDown, viewportHeight);
                }
            }
            // compute boundary unique key
            const boundaryKey = [_stickyComputed.boundary.top, _stickyComputed.boundary.height].join(',');
            // ensure stickies computed to use same boundary instance
            if (boundariesMap[boundaryKey]) {
                _stickyComputed.boundary = boundariesMap[boundaryKey];
            }
            else {
                boundariesMap[boundaryKey] = _stickyComputed.boundary;
            }
            // skip sticky sibling when is disabled
            if (_stickyComputed.disabled) {
                continue;
            }
            // set sticky sibling height as max boundary offset when it unstacked
            if (_stickyComputed.boundary.unstacked) {
                if (_directionDown) {
                    if (_sticky.height > _stickyComputed.boundary.offsetBottom) {
                        _stickyComputed.boundary.offsetBottom = _sticky.height;
                    }
                }
                else {
                    if (_sticky.height > _stickyComputed.boundary.offsetTop) {
                        _stickyComputed.boundary.offsetTop = _sticky.height;
                    }
                }
                // add sticky sibling height to right boundary offset when it stacked
            }
            else {
                if (_directionDown) {
                    _stickyComputed.boundary.offsetBottom += _sticky.height;
                }
                else {
                    _stickyComputed.boundary.offsetTop += _sticky.height;
                }
            }
            // pushforce offset spacer as sticky siblings
            if (_sticky === offsetSpacer) {
                stickiesComputed.push(_stickyComputed);
                continue;
            }
            // pushforce opposite offset spacer as opposite sticky siblings
            if (_sticky === oppositeOffsetSpacer) {
                oppositeStickiesComputed.push(_stickyComputed);
                continue;
            }
            // collect stickyComputed siblings
            if (
            // when _stickyComputed isn't stickyComputed
            _stickyComputed !== stickyComputed
                // and its sticked line intersects top of stickyComputed sticked line
                && stickyComputed.sticked.top >= _stickyComputed.sticked.top
                && stickyComputed.sticked.top <= _stickyComputed.sticked.top + _stickyComputed.sticked.height
                // and its top is before stickyComputed top according to its own position
                && (_stickyComputed.directionDown
                    ? _stickyComputed.top < stickyComputed.top
                    : _stickyComputed.top > stickyComputed.top)) {
                // and its position equals to stickyComputed position
                if (_stickyComputed.positionBottom === stickyComputed.positionBottom) {
                    stickiesComputed.push(_stickyComputed);
                    // else _stickyComputed is an opposite sibling
                }
                else {
                    oppositeStickiesComputed.push(_stickyComputed);
                }
            }
        }
        // sort opposite siblings according to their respective sortPoint and boundary top
        oppositeStickiesComputed.sort(compareStickiesComputed);
        // sort siblings according to their respective sortPoint and boundary top
        stickiesComputed.sort(compareStickiesComputed);
        // add stickyComputed in last position
        stickiesComputed.push(stickyComputed);
        return {
            container,
            boundaries: boundariesMap,
            oppositeStickies: oppositeStickiesComputed,
            stickies: stickiesComputed,
            sticky,
            stickyComputed,
            viewportHeight,
        };
    }
    _collectStickySnaps(container, stickies, position, viewportHeight) {
        const stickySnaps = [];
        if (position !== 'both') {
            const positionBottom = isStickyPositionBottom(position);
            stickies = stickies.filter(sticky => isStickyPositionBottom(sticky.position) === positionBottom);
        }
        for (const sticky of stickies) {
            const stickySnap = this.snapSticky(container, stickies, sticky, viewportHeight);
            if (!stickySnap.stickyComputed.disabled) {
                stickySnaps.push(stickySnap);
            }
        }
        return stickySnaps;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyEngine, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyEngine, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyEngine, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const NGX_STICKY_BASE_CONTAINER_CONFIG_SCHEMA = {
    disabled: {
        aliasKey: 'stickyDisabled',
        defaultValue: false,
        coercion: coerceBooleanProperty,
    },
    offsetTop: {
        aliasKey: 'stickyOffsetTop',
        defaultValue: 0,
        coercion: coerceNumberProperty,
    },
    offsetBottom: {
        aliasKey: 'stickyOffsetBottom',
        defaultValue: 0,
        coercion: coerceNumberProperty,
    },
    unstacked: {
        aliasKey: 'stickyUnstacked',
        defaultValue: false,
        coercion: coerceBooleanProperty,
    },
};
/**
 * Abstract sticky container directive.
 */
class NgxStickyBaseContainerDirective extends NgxStickyBaseContainerController {
    get config() {
        return this.config$.getValue();
    }
    get disabled() {
        return this.config.disabled;
    }
    constructor(containerParent, stickyEngine, ngZone, _win) {
        super();
        this.containerParent = containerParent;
        this.stickyEngine = stickyEngine;
        this.ngZone = ngZone;
        this._win = _win;
        /** Inputs config */
        this.config$ = new ConfigSubject(NGX_STICKY_BASE_CONTAINER_CONFIG_SCHEMA);
        /** Emits when the service is destroyed. */
        this.destroyed$ = new Subject();
        /** Emits when updateStickies() is called */
        this._updateStickies$ = new Subject();
        if (this.containerParent) {
            this.containerParent.registerContainer(this);
        }
        this._getDocumentHeight = getDocumentHeightFactory(this._win);
        this._getDocumentWidth = getDocumentWidthFactory(this._win);
    }
    ngOnChanges(changes) {
        this.config$.nextChanges(changes);
    }
    ngOnDestroy() {
        if (this.containerParent) {
            this.containerParent.unregisterContainer(this);
        }
        if (!this.destroyed$.isStopped) {
            this.destroyed$.next();
            this.destroyed$.complete();
        }
        this._destroyMonitoring();
    }
    beforeRefresh(fastUpdate) {
        if (!fastUpdate) {
            this._container = null;
        }
    }
    createScrollPlan(target, optionsOrExtraOffsetTop) {
        // const options = typeof optionsOrExtraOffsetTop === 'number' ? { extraOffsetTop: optionsOrExtraOffsetTop } : optionsOrExtraOffsetTop;
        const options = optionsOrExtraOffsetTop;
        const scrollPlan = [];
        if (!this._win) {
            return scrollPlan;
        }
        if (!this._scrollToFn) {
            this._scrollToFn = scrollToFactory(this._win, this.element);
        }
        const scrollToFn = this._scrollToFn;
        let containsElement = false;
        if (typeof target === 'string') {
            const elementAsNumber = parseFloat(target);
            if (!isNaN(elementAsNumber)) {
                target = elementAsNumber;
            }
            else {
                if (this.element) {
                    target = this.element.querySelector(target);
                }
                else {
                    target = this._win.document.querySelector(target);
                }
                if (target) {
                    containsElement = true;
                }
            }
        }
        if (typeof target === 'number' && !isNaN(target)) {
            const elementTop = this.fixViewportTop(target, options);
            scrollPlan.push({
                scrollToOptions: { left: this.getViewportLeft(), top: elementTop },
                scrollToFn,
            });
            return scrollPlan;
        }
        // if (!target || !(target instanceof HTMLElement)) {
        if (!target || !(target.tagName)) {
            return scrollPlan;
        }
        containsElement = containsElement || !this.element || this.element.contains(target);
        if (!containsElement) {
            return scrollPlan;
        }
        let targetContainer;
        let targetContainerScrollPlan;
        for (const containerController of this.containers) {
            const containerScrollPlan = containerController.createScrollPlan(target, options);
            if (containerScrollPlan.length) {
                targetContainerScrollPlan = containerScrollPlan;
                targetContainer = containerController;
                break;
            }
        }
        const containerScrollable = !this.element || isElementScrollableY(this._win, this.element);
        if (containerScrollable) {
            const targetLine = targetContainer
                ? targetContainer.getContainer()
                : getElementAbsoluteRect(target);
            const targetTopFixed = this.fixViewportTop(targetLine.top, options);
            scrollPlan.push({
                scrollToFn,
                scrollToOptions: { left: this.getViewportLeft(), top: targetTopFixed },
            });
        }
        if (targetContainerScrollPlan) {
            scrollPlan.push(...targetContainerScrollPlan);
        }
        return scrollPlan;
    }
    disableStickies() {
        this.config$.nextKeyValue('disabled', true, { skipCoercion: true });
    }
    enableStickies() {
        this.config$.nextKeyValue('disabled', false, { skipCoercion: true });
    }
    getContainer() {
        if (!this._container) {
            this._container = this._computeContainer();
        }
        return this._container;
    }
    getViewportHeight() {
        return getWindowViewportHeight(this._win);
    }
    getViewportLeft() {
        return getWindowViewportLeft(this._win);
    }
    getViewportTop() {
        return getWindowViewportTop(this._win);
    }
    registerSticky(sticky) {
        super.registerSticky(sticky);
        if (this.stickies.length) {
            this._initMonitoring();
        }
    }
    unregisterSticky(sticky) {
        super.unregisterSticky(sticky);
        if (!this.stickies.length) {
            this._destroyMonitoring();
        }
    }
    scrollToTop(target, optionsOrExtraOffsetTop) {
        // const options = typeof optionsOrExtraOffsetTop === 'number' ? { extraOffsetTop: optionsOrExtraOffsetTop } : optionsOrExtraOffsetTop;
        const options = optionsOrExtraOffsetTop;
        const scrollPlan = this.createScrollPlan(target, options);
        for (const scrollStep of scrollPlan) {
            scrollStep.scrollToFn(scrollStep.scrollToOptions);
        }
    }
    updateStickies(fastUpdate) {
        // intercept update stickies to throttle calls
        this._updateStickies$.next(!!fastUpdate);
    }
    _computeContainer() {
        const config = this.config$.getValue();
        const containerRect = this.element ? getElementAbsoluteRect(this.element) : null;
        return {
            disabled: this.disabled,
            height: containerRect ? this.element.scrollHeight : this._getDocumentHeight(),
            left: containerRect ? containerRect.left : 0,
            offsetBottom: config.offsetBottom,
            offsetTop: config.offsetTop,
            top: containerRect ? containerRect.top : 0,
            unstacked: config.unstacked,
            width: containerRect ? this.element.scrollWidth : this._getDocumentWidth(),
        };
    }
    _createMonitoringObservable() {
        if (!this._win) {
            return of();
        }
        const updateStickiesFast$ = merge(this._updateStickies$.pipe(filter(fastUpdate => fastUpdate)), fromEvent(this.element || this._win, 'scroll', { passive: true })).pipe(throttleTime(0, animationFrameScheduler, { leading: true, trailing: true }));
        const updateStickiesFull$ = merge(this.config$, this._updateStickies$.pipe(filter(fastUpdate => !fastUpdate)), fromEvent(this._win.document, 'DOMContentLoaded', { passive: true }), fromEvent(this._win, 'load', { passive: true }), fromEvent(this._win, 'pageshow', { passive: true }), 
        // fromEvent(this._win, 'visibilitychange', { passive: true }),
        fromEvent(this._win, 'orientationchange', { passive: true }), fromEvent(this._win, 'resize', { capture: true, passive: true })).pipe(debounceTime(0, animationFrameScheduler));
        return updateStickiesFull$.pipe(takeUntil(this.destroyed$), switchMap(() => {
            return updateStickiesFast$.pipe(map(() => true), startWith(false), takeUntil(this.destroyed$));
        }));
    }
    _destroyMonitoring() {
        if (this._monitoring) {
            this._monitoring.unsubscribe();
            this._monitoring = null;
        }
    }
    _initMonitoring() {
        if (!this._win || this._monitoring) {
            return;
        }
        this.ngZone.runOutsideAngular(() => {
            this._monitoring = this._createMonitoringObservable().pipe(takeUntil(this.destroyed$)).subscribe(fastUpdate => {
                this._updateStickies(fastUpdate);
            });
            // fromMediaQuery(this._win, 'print').pipe(
            //   takeUntil(this.destroyed$),
            // ).subscribe(mqlEvent => {
            //   if (mqlEvent.matches) {
            //     this.disableStickies();
            //   } else {
            //     this.enableStickies();
            //   }
            // });
        });
    }
    _updateStickies(fastUpdate) {
        super.updateStickies(fastUpdate);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyBaseContainerDirective, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.0", type: NgxStickyBaseContainerDirective, inputs: { stickyDisabled: "stickyDisabled", stickyOffsetBottom: "stickyOffsetBottom", stickyOffsetTop: "stickyOffsetTop", stickyUnstacked: "stickyUnstacked" }, usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyBaseContainerDirective, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: undefined }, { type: NgxStickyEngine }, { type: i0.NgZone }, { type: Window }], propDecorators: { stickyDisabled: [{
                type: Input
            }], stickyOffsetBottom: [{
                type: Input
            }], stickyOffsetTop: [{
                type: Input
            }], stickyUnstacked: [{
                type: Input
            }] } });

const NGX_STICKY_WINDOW = new InjectionToken('NGX_STICKY_WINDOW', {
    providedIn: 'root',
    factory: () => typeof window !== 'undefined' ? window : null,
});

/**
 * Defines the sticky root container which is used to manage sticky without container.
 */
class NgxStickyRootContainerController extends NgxStickyBaseContainerDirective {
    constructor(stickyEngine, ngZone, _win) {
        super(null, stickyEngine, ngZone, _win);
        // root container never has parent container
        this.containerParent = null;
        // root container never has element
        this.element = null;
        // define aliases to input handly because the root container isn't an angular directive
        Object.keys(this.config$._aliases).forEach((alias) => {
            const configKey = this.config$._aliases[alias];
            Object.defineProperty(this, alias, {
                get: () => this.config$.getKeyValue(configKey),
                set: (value) => this.config$.nextKeyValue(configKey, value),
            });
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyRootContainerController, deps: [{ token: NgxStickyEngine }, { token: i0.NgZone }, { token: NGX_STICKY_WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyRootContainerController, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyRootContainerController, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: NgxStickyEngine }, { type: i0.NgZone }, { type: Window, decorators: [{
                    type: Inject,
                    args: [NGX_STICKY_WINDOW]
                }] }] });

/**
 * Defines a sticky container.
 */
class NgxStickyContainerDirective extends NgxStickyBaseContainerDirective {
    /**
     * Returns HTMLElement of the container.
     */
    get element() {
        return this.elementRef.nativeElement;
    }
    constructor(rootContainer, stickyContainerParent, stickyEngine, ngZone, elementRef, _win) {
        // use root container when boundary isn't in container
        super(stickyContainerParent || rootContainer, stickyEngine, ngZone, _win);
        this.rootContainer = rootContainer;
        this.stickyContainerParent = stickyContainerParent;
        this.elementRef = elementRef;
    }
    getViewportHeight() {
        return this.element.offsetHeight;
    }
    getViewportLeft() {
        return this.element.scrollLeft;
    }
    getViewportTop() {
        return this.element.scrollTop;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyContainerDirective, deps: [{ token: NgxStickyRootContainerController }, { token: forwardRef(() => NgxStickyContainerDirective), optional: true, skipSelf: true }, { token: NgxStickyEngine }, { token: i0.NgZone }, { token: i0.ElementRef }, { token: NGX_STICKY_WINDOW }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.0", type: NgxStickyContainerDirective, selector: "[ngxStickyContainer], [ngx-sticky-container], ngx-sticky-container", exportAs: ["ngxStickyContainer"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxStickyContainer], [ngx-sticky-container], ngx-sticky-container',
                    exportAs: 'ngxStickyContainer',
                }]
        }], ctorParameters: () => [{ type: NgxStickyRootContainerController }, { type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [forwardRef(() => NgxStickyContainerDirective)]
                }] }, { type: NgxStickyEngine }, { type: i0.NgZone }, { type: i0.ElementRef }, { type: Window, decorators: [{
                    type: Inject,
                    args: [NGX_STICKY_WINDOW]
                }] }] });

/** Coerces a data-bound value (typically a string) to intersection thresholds. */
function coerceIntersectionThresholds(thresholds) {
    if (typeof thresholds === 'number') {
        return [thresholds];
    }
    if (typeof thresholds === 'string') {
        return thresholds
            .split(',')
            .map(x => (parseFloat(x) || 0))
            .sort();
    }
    if (!thresholds) {
        return [0, 1];
    }
    if (!Array.isArray(thresholds)) {
        thresholds = [thresholds];
    }
    return [...thresholds].sort();
}
/**
 * Returns threshold crossed for a given ratio change.
 *
 * @param thresholds Thresholds
 * @param oldRatio Old ratio
 * @param newRatio New ratio
 * @returns Threshold crossed or `undefined`
 */
function getCrossedThreshold(thresholds, oldRatio, newRatio) {
    let crossedThreshold;
    for (const threshold of thresholds) {
        if (
        // threshold is perfect-crossed by old ratio
        threshold === oldRatio
            // or threshold is perfect-crossed by new ratio
            || threshold === newRatio
            // or threshold is crossed by new ratio and old ratio (there are on the opposite sides)
            || threshold < oldRatio !== threshold < newRatio) {
            crossedThreshold = threshold;
            break;
        }
    }
    return crossedThreshold;
}

const NGX_BASE_INTERSECTION_CONFIG_SCHEMA = {
    disabled: {
        aliasKey: 'intersectionDisabled',
        defaultValue: false,
        coercion: coerceBooleanProperty,
    },
    thresholds: {
        aliasKey: 'intersectionThresholds',
        defaultValue: [0, 1],
        coercion: coerceIntersectionThresholds,
    },
};
class NgxInViewportDirective {
    get container() {
        return this._container;
    }
    get config() {
        return this.config$.getValue();
    }
    get disabled() {
        return this.config.disabled;
    }
    /**
     * State of the intersection.
     */
    get state() {
        return this._intersectionState;
    }
    constructor(rootContainer, stickyContainer, elementRef, ngZone, _win) {
        this.rootContainer = rootContainer;
        this.stickyContainer = stickyContainer;
        this.elementRef = elementRef;
        this.ngZone = ngZone;
        this._win = _win;
        /**
         * Emit intersection.
         */
        this.intersection = new EventEmitter();
        /**
         * Emit intersection computation.
         */
        this.intersectionComputation = new EventEmitter();
        /**
         * Emit intersection state.
         */
        this.intersectionState = new EventEmitter();
        /**
         * Emit intersection threshold.
         */
        this.intersectionThreshold = new EventEmitter();
        /** Inputs config */
        this.config$ = new ConfigSubject(NGX_BASE_INTERSECTION_CONFIG_SCHEMA);
        /** Emits when the component is destroyed. */
        this._destroyed$ = new Subject();
        /** Emits when refresh() is called */
        this._refresh$ = new Subject();
        // use root container when sticky isn't in container
        this._container = stickyContainer || rootContainer;
        // register in parent container for first update calls
        this.container.registerIntersection(this);
    }
    ngOnChanges(changes) {
        this.config$.nextChanges(changes);
    }
    ngAfterViewInit() {
        this._initMonitoring();
    }
    ngOnDestroy() {
        this.container.unregisterIntersection(this);
        if (!this._destroyed$.isStopped) {
            this._destroyed$.next();
            this._destroyed$.complete();
        }
        this._destroyMonitoring();
    }
    beforeRefresh(fastUpdate) {
        if (!fastUpdate) {
            this._intersection = null;
        }
    }
    disableIntersection() {
        this.config$.nextKeyValue('disabled', true, { skipCoercion: true });
    }
    enableIntersection() {
        this.config$.nextKeyValue('disabled', false, { skipCoercion: true });
    }
    getIntersection() {
        if (!this._intersection) {
            this._intersection = this._computeIntersection();
        }
        return this._intersection;
    }
    refresh(computation) {
        this._refresh$.next(computation);
    }
    update(fastUpdate) {
        this.container.updateStickies(fastUpdate);
    }
    _computeIntersection() {
        const config = this.config$.getValue();
        const elementRect = getElementAbsoluteRect(this.elementRef.nativeElement);
        return {
            disabled: config.disabled,
            height: elementRect.height,
            top: elementRect.top,
            thresholds: config.thresholds,
        };
    }
    /**
     * Create intersection monitoring observable.
     */
    _createMonitoringObservable() {
        return this.config$.pipe(debounceTime(0, animationFrameScheduler), map(() => false), takeUntil(this._destroyed$));
    }
    /**
     * Destroy intersection monitoring subscription.
     */
    _destroyMonitoring() {
        if (this._monitoring) {
            this._monitoring.unsubscribe();
            this._monitoring = null;
        }
    }
    /**
     * Init intersection monitoring.
     */
    _initMonitoring() {
        if (!this._win || this._monitoring) {
            return;
        }
        this.ngZone.runOutsideAngular(() => {
            const handleRefreshSubscription = this._refresh$.pipe(distinctUntilChanged(), takeUntil(this._destroyed$)).subscribe(computation => {
                this._refreshIntersection(computation);
            });
            const triggerUpdateSubscription = this._createMonitoringObservable().pipe(takeUntil(this._destroyed$)).subscribe(fastUpdate => {
                this.update(fastUpdate);
            });
            this._monitoring = new Subscription();
            this._monitoring.add(handleRefreshSubscription);
            this._monitoring.add(triggerUpdateSubscription);
        });
    }
    /**
     * Refresh intersection with given computation.
     *
     * @param computation Intersection state computation
     */
    _refreshIntersection(computation) {
        this._intersectionComputation = computation;
        this.intersectionComputation.next(computation);
        if (computation.state !== this._intersectionState) {
            this.ngZone.run(() => {
                this._intersectionState = computation.state;
                this.intersectionState.next(computation.state);
            });
        }
        const oldEntry = this._intersectionCrossed;
        const newEntry = computation;
        const oldRatio = oldEntry ? oldEntry.ratio : 0;
        const newRatio = newEntry.ratio;
        // ignore when ratios are unchanged
        if (oldEntry && oldRatio === newRatio) {
            return;
        }
        const crossedThreshold = getCrossedThreshold(computation.snap.intersection.thresholds, oldRatio, newRatio);
        // ignore when no treshold is crossed
        if (isNaN(crossedThreshold)) {
            return;
        }
        this._intersectionCrossed = newEntry;
        this.ngZone.run(() => {
            this._intersectionThreshold = crossedThreshold;
            this.intersectionThreshold.next(crossedThreshold);
            this.intersection.emit(newEntry);
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxInViewportDirective, deps: [{ token: NgxStickyRootContainerController }, { token: forwardRef(() => NgxStickyContainerDirective), optional: true }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: NGX_STICKY_WINDOW }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.0", type: NgxInViewportDirective, selector: "[ngxInViewport], [ngx-in-viewport], ngx-in-viewport", inputs: { intersectionDisabled: "intersectionDisabled", intersectionThresholds: "intersectionThresholds" }, outputs: { intersection: "intersection", intersectionComputation: "intersectionComputation", intersectionState: "intersectionState", intersectionThreshold: "intersectionThreshold" }, exportAs: ["ngxInViewport"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxInViewportDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxInViewport], [ngx-in-viewport], ngx-in-viewport',
                    exportAs: 'ngxInViewport',
                }]
        }], ctorParameters: () => [{ type: NgxStickyRootContainerController }, { type: NgxStickyContainerDirective, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [forwardRef(() => NgxStickyContainerDirective)]
                }] }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: Window, decorators: [{
                    type: Inject,
                    args: [NGX_STICKY_WINDOW]
                }] }], propDecorators: { intersectionDisabled: [{
                type: Input
            }], intersectionThresholds: [{
                type: Input
            }], intersection: [{
                type: Output
            }], intersectionComputation: [{
                type: Output
            }], intersectionState: [{
                type: Output
            }], intersectionThreshold: [{
                type: Output
            }] } });

/**
 * Abstract sticky boundary controller.
 */
class NgxStickyBaseBoundaryController {
    updateStickies(fastUpdate) {
        this.container.updateStickies(fastUpdate);
    }
}

/**
 * Abstract sticky controller.
 */
class NgxStickyBaseController {
    update(fastUpdate) {
        // all stickies need to be updated when one of them changed
        this.container.updateStickies(fastUpdate);
    }
}

const NGX_STICKY_BASE_BOUNDARY_CONFIG_SCHEMA = {
    unstacked: {
        aliasKey: 'stickyUnstacked',
        defaultValue: false,
        coercion: coerceBooleanProperty,
    },
};
/**
 * Defines a sticky boundary.
 */
class NgxStickyBoundaryDirective extends NgxStickyBaseBoundaryController {
    get config() {
        return this.config$.getValue();
    }
    get container() {
        return this._container;
    }
    constructor(rootContainer, stickyContainer, elementRef, ngZone, _win) {
        super();
        this.rootContainer = rootContainer;
        this.stickyContainer = stickyContainer;
        this.elementRef = elementRef;
        this.ngZone = ngZone;
        this._win = _win;
        /** Inputs config */
        this.config$ = new ConfigSubject(NGX_STICKY_BASE_BOUNDARY_CONFIG_SCHEMA);
        /** Emits when the component is destroyed. */
        this._destroyed$ = new Subject();
        // use root container when boundary isn't in container
        this._container = stickyContainer || rootContainer;
        // register boundary in container for first update calls
        this.container.registerBoundary(this);
    }
    ngOnChanges(changes) {
        this.config$.nextChanges(changes);
    }
    ngOnInit() {
        this._initMonitoring();
    }
    ngOnDestroy() {
        this.container.unregisterBoundary(this);
        if (!this._destroyed$.isStopped) {
            this._destroyed$.next();
            this._destroyed$.complete();
        }
        this._destroyMonitoring();
    }
    beforeRefresh(fastUpdate) {
        if (!fastUpdate) {
            this._boundary = null;
        }
    }
    getBoundary() {
        if (!this._boundary) {
            this._boundary = this._computeBoundary();
        }
        return this._boundary;
    }
    _computeBoundary() {
        const boundary = getElementAbsoluteRect(this.elementRef.nativeElement);
        if (this._win) {
            const boundaryStyle = this._win.getComputedStyle(this.elementRef.nativeElement);
            const paddingTop = parseFloat(boundaryStyle.paddingTop) || 0;
            const paddingBottom = parseFloat(boundaryStyle.paddingBottom) || 0;
            // subtract paddings from computed boundary line
            boundary.top += paddingTop;
            boundary.height -= paddingTop + paddingBottom;
        }
        boundary.unstacked = this.config.unstacked;
        return boundary;
    }
    _createMonitoringObservable() {
        return this.config$.pipe(debounceTime(0, animationFrameScheduler), map(() => false), takeUntil(this._destroyed$));
    }
    _destroyMonitoring() {
        if (this._monitoring) {
            this._monitoring.unsubscribe();
            this._monitoring = null;
        }
    }
    _initMonitoring() {
        if (!this._win || this._monitoring) {
            return;
        }
        this.ngZone.runOutsideAngular(() => {
            this._monitoring = this._createMonitoringObservable().pipe(takeUntil(this._destroyed$)).subscribe(fastUpdate => {
                this.updateStickies(fastUpdate);
            });
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyBoundaryDirective, deps: [{ token: NgxStickyRootContainerController }, { token: forwardRef(() => NgxStickyContainerDirective), optional: true }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: NGX_STICKY_WINDOW }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.0", type: NgxStickyBoundaryDirective, selector: "[ngxStickyBoundary], [ngx-sticky-boundary], ngx-sticky-boundary", inputs: { stickyUnstacked: "stickyUnstacked" }, exportAs: ["ngxStickyBoundary"], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyBoundaryDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxStickyBoundary], [ngx-sticky-boundary], ngx-sticky-boundary',
                    exportAs: 'ngxStickyBoundary',
                }]
        }], ctorParameters: () => [{ type: NgxStickyRootContainerController }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [forwardRef(() => NgxStickyContainerDirective)]
                }] }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: Window, decorators: [{
                    type: Inject,
                    args: [NGX_STICKY_WINDOW]
                }] }], propDecorators: { stickyUnstacked: [{
                type: Input
            }] } });

/**
 * Create observable which emit image events.
 *
 * @param element Element
 * @returns Observable on image events
 */
function fromImageEvents(element, options) {
    return new Observable(subscriber => {
        const images = [];
        const next = (errorOrLoadEvent) => {
            removeImage(errorOrLoadEvent.target);
            subscriber.next(errorOrLoadEvent);
            if (!images.length && !subscriber.closed) {
                subscriber.complete();
            }
        };
        const onload = (loadEvent) => {
            next(loadEvent);
        };
        const onerror = (errorEvent) => {
            next(errorEvent);
        };
        const addImage = (image) => {
            if (image.complete || images.indexOf(image) !== -1) {
                return;
            }
            images.push(image);
            image.addEventListener('load', onload, options);
            image.addEventListener('error', onerror, options);
        };
        const removeImage = (image) => {
            const imageIndex = images.indexOf(image);
            if (imageIndex === -1) {
                return;
            }
            images.splice(imageIndex, 1);
            image.removeEventListener('load', onload, options);
            image.removeEventListener('error', onerror, options);
        };
        if (element.tagName === 'IMG' || element.tagName === 'img') {
            addImage(element);
        }
        else {
            element.querySelectorAll('img').forEach(addImage);
        }
        if (!images.length) {
            subscriber.complete();
        }
        return () => [...images].forEach(removeImage);
    });
}

/** Global registry for all dynamically-created, injected media queries. */
const mediaQueriesForWebkitCompatibility = new Set();
/** Style tag that holds all of the dynamically-created media queries. */
let mediaQueryStyleNode;
/**
 * Create observable which emit media query events.
 *
 * @param win Window reference
 * @param query Media query
 * @returns Observable on media query events
 */
function fromMediaQuery(win, query, nonce) {
    return new Observable(subscriber => {
        if (!win?.matchMedia) {
            return;
        }
        if (!hasEmptyStyleRule(query) && isEmptyStyleRuleNeeded(win)) {
            createEmptyStyleRule(win, query, nonce);
        }
        const mql = win.matchMedia(query);
        const initEvent = {
            matches: mql.matches,
            media: query,
        };
        const initMqlEvent = typeof MediaQueryListEvent !== 'undefined'
            ? new MediaQueryListEvent('change', initEvent)
            : { type: 'change', ...initEvent };
        subscriber.next(initMqlEvent);
        const onQueryChange = (mqlEvent) => subscriber.next(mqlEvent);
        mql.addListener(onQueryChange);
        return () => mql.removeListener(onQueryChange);
    });
}
function hasEmptyStyleRule(query) {
    return mediaQueriesForWebkitCompatibility.has(query);
}
/**
 * Creates an empty stylesheet that is used to work around browser inconsistencies related to
 * `matchMedia`. At the time of writing, it handles the following cases:
 * 1. On WebKit browsers, a media query has to have at least one rule in order for `matchMedia`
 * to fire. We work around it by declaring a dummy stylesheet with a `@media` declaration.
 * 2. In some cases Blink browsers will stop firing the `matchMedia` listener if none of the rules
 * inside the `@media` match existing elements on the page. We work around it by having one rule
 * targeting the `body`. See https://github.com/angular/components/issues/23546.
 * @see https://github.com/angular/components/blob/16.2.6/src/cdk/layout/media-matcher.ts#L49-L82
 */
function createEmptyStyleRule(win, query, nonce) {
    if (mediaQueriesForWebkitCompatibility.has(query)) {
        return;
    }
    try {
        if (!mediaQueryStyleNode) {
            mediaQueryStyleNode = win.document.createElement('style');
            if (nonce) {
                mediaQueryStyleNode.nonce = nonce;
            }
            mediaQueryStyleNode.setAttribute('type', 'text/css');
            win.document.head.appendChild(mediaQueryStyleNode);
        }
        if (mediaQueryStyleNode.sheet) {
            mediaQueryStyleNode.sheet.insertRule(`@media ${query} {body{ }}`, 0);
            mediaQueriesForWebkitCompatibility.add(query);
        }
    }
    catch (e) {
        console.error(e);
    }
}
// @see https://github.com/angular/components/blob/16.2.6/src/cdk/platform/platform.ts#L33-L64
function isEmptyStyleRuleNeeded(win) {
    if (!win?.navigator?.userAgent) {
        return false;
    }
    // Whether the current platform supports the V8 Break Iterator. The V8 check
    // is necessary to detect all Blink based browsers.
    let hasV8BreakIterator;
    // We need a try/catch around the reference to `Intl`, because accessing it in some cases can
    // cause IE to throw. These cases are tied to particular versions of Windows and can happen if
    // the consumer is providing a polyfilled `Map`. See:
    // https://github.com/Microsoft/ChakraCore/issues/3189
    // https://github.com/angular/components/issues/15687
    try {
        hasV8BreakIterator = !!(win.Intl?.v8BreakIterator);
    }
    catch {
        hasV8BreakIterator = false;
    }
    /** Whether the current browser is Microsoft Edge. */
    const EDGE = /(edge)/i.test(win.navigator.userAgent);
    /** Whether the current rendering engine is Microsoft Trident. */
    const TRIDENT = /(msie|trident)/i.test(win.navigator.userAgent);
    // EdgeHTML and Trident mock Blink specific things and need to be excluded from this check.
    /** Whether the current rendering engine is Blink. */
    const BLINK = !!(win.chrome || hasV8BreakIterator) && !!win.CSS && EDGE && TRIDENT;
    // Webkit is part of the userAgent in EdgeHTML, Blink and Trident. Therefore we need to
    // ensure that Webkit runs standalone and is not used as another engine's base.
    /** Whether the current rendering engine is WebKit. */
    const WEBKIT = /AppleWebKit/i.test(win.navigator.userAgent) && !BLINK && !EDGE && !TRIDENT;
    return WEBKIT || BLINK;
}

class NgxStickySpotDirective {
    constructor() {
        this.cssClassStickySpot = true;
        this.cssStylePosition = 'absolute';
        this._stickySpotHeight = 1;
    }
    get stickySpotHeight() { return this._stickySpotHeight; }
    set stickySpotHeight(value) { this._stickySpotHeight = coerceNumberProperty(value); }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickySpotDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.0", type: NgxStickySpotDirective, selector: "[ngxStickySpot], [ngx-sticky-spot], ngx-sticky-spot", inputs: { stickySpotHeight: "stickySpotHeight" }, host: { properties: { "class.ngx-sticky-spot": "this.cssClassStickySpot", "style.position": "this.cssStylePosition", "style.height.px": "this.stickySpotHeight" } }, exportAs: ["ngxStickySpot"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickySpotDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxStickySpot], [ngx-sticky-spot], ngx-sticky-spot',
                    exportAs: 'ngxStickySpot',
                }]
        }], propDecorators: { cssClassStickySpot: [{
                type: HostBinding,
                args: ['class.ngx-sticky-spot']
            }], cssStylePosition: [{
                type: HostBinding,
                args: ['style.position']
            }], stickySpotHeight: [{
                type: HostBinding,
                args: ['style.height.px']
            }, {
                type: Input
            }] } });

const NGX_STICKY_BASE_CONFIG_SCHEMA = {
    classes: {
        aliasKey: 'stickyClasses',
        defaultValue: false,
        coercion: coerceBooleanProperty,
    },
    disabled: {
        aliasKey: 'stickyDisabled',
        defaultValue: false,
        coercion: coerceBooleanProperty,
    },
    direction: {
        aliasKey: 'stickyDirection',
        defaultValue: 'down',
        coercion: coerceStickyDirection,
    },
    height: {
        aliasKey: 'stickyHeight',
        defaultValue: 0,
        coercion: coerceNumberProperty,
    },
    // orbit: {
    //   aliasKey: 'stickyOrbit',
    //   defaultValue: false,
    //   coercion: coerceBooleanProperty,
    // },
    position: {
        aliasKey: 'stickyPosition',
        defaultValue: 'top',
        coercion: coerceStickyPosition,
    },
    spacer: {
        aliasKey: 'stickySpacer',
        defaultValue: null,
    },
    spot: {
        aliasKey: 'stickySpot',
        defaultValue: null,
    },
    spotHeight: {
        aliasKey: 'stickySpotHeight',
        defaultValue: 0,
        coercion: coerceNumberProperty,
    },
};
/**
 * Defines a sticky.
 */
class NgxStickyDirective extends NgxStickyBaseController {
    get attrDataStickyState() { return !this.stickyParent ? this.state : null; }
    get cssClassSticky() { return !this.stickyParent && this.config.classes; }
    get cssClassStickyNormal() { return this.cssClassSticky && this.state === 'normal'; }
    get cssClassStickySticked() { return this.cssClassSticky && this.state === 'sticked'; }
    get cssClassStickyStucked() { return this.cssClassSticky && this.state === 'stucked'; }
    get cssClassStickyDisabled() { return this.cssClassSticky && this.disabled; }
    // @HostBinding('class.ngx-sticky--spot')
    // get cssClassStickySpot() { return this.cssClassSticky && !!this.config.spot; }
    // @HostBinding('class.ngx-sticky--position-top')
    // get cssClassStickyPositionTop() { return this.cssClassSticky && !isStickyPositionBottom(this.config.position); }
    // @HostBinding('class.ngx-sticky--position-bottom')
    // get cssClassStickyPositionBottom() { return this.cssClassSticky && isStickyPositionBottom(this.config.position); }
    // @HostBinding('class.ngx-sticky--direction-up')
    // get cssClassStickyDirectionUp() { return this.cssClassSticky && !isStickyDirectionDown(this.config.direction); }
    // @HostBinding('class.ngx-sticky--direction-down')
    // get cssClassStickydirectionDown() { return this.cssClassSticky && isStickyDirectionDown(this.config.direction); }
    get boundary() {
        return this._boundary;
    }
    get container() {
        return this._container;
    }
    get config() {
        return this.config$.getValue();
    }
    get disabled() {
        return this.config.disabled;
    }
    /**
     * State of the sticky.
     */
    get state() {
        return this._stickyState;
    }
    constructor(rootContainer, stickyContainer, stickyBoundary, stickyParent, elementRef, renderer, 
    // readonly changeDetectorRef: ChangeDetectorRef,
    ngZone, _win) {
        super();
        this.rootContainer = rootContainer;
        this.stickyContainer = stickyContainer;
        this.stickyBoundary = stickyBoundary;
        this.stickyParent = stickyParent;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.ngZone = ngZone;
        this._win = _win;
        /**
         * Emit sticky computation.
         */
        this.stickyComputation = new EventEmitter();
        /**
         * Emit sticky state.
         */
        this.stickyState = new EventEmitter();
        /** Inputs config */
        this.config$ = new ConfigSubject(NGX_STICKY_BASE_CONFIG_SCHEMA);
        /** Emits when the component is destroyed. */
        this._destroyed$ = new Subject();
        /** Emits when refresh() is called */
        this._refresh$ = new Subject();
        /** Sticky element state which reflect last call of _refreshStickyElement() */
        this._stickyElementState = null;
        /** Sticky element state which reflect last sticky state output */
        this._stickyState = null;
        // use root container when sticky isn't in container
        this._container = stickyContainer || rootContainer;
        // ensure sticky boundary is in same container
        this._boundary = stickyBoundary && stickyBoundary.container === this._container ? stickyBoundary : null;
        // register sticky in container only if isn't in another sticky
        if (!this.stickyParent) {
            // register in parent container for first update calls
            this.container.registerSticky(this);
        }
    }
    ngOnChanges(changes) {
        this.config$.nextChanges(changes);
    }
    ngAfterViewInit() {
        // avoid sticky initialization when sticky has a parent
        if (this._preventNestedStickyError()) {
            return;
        }
        this._initMonitoring();
    }
    ngOnDestroy() {
        this.container.unregisterSticky(this);
        if (!this._destroyed$.isStopped) {
            this._destroyed$.next();
            this._destroyed$.complete();
        }
        this._destroyMonitoring();
    }
    beforeRefresh(fastUpdate) {
        if (!fastUpdate) {
            this._sticky = null;
            // IMPORTANT: refresh sticky element to its normal state here helps
            // `NgxStickyBaseContainerController::updateStickies` to process full update
            // with all its stickies being freshly repainted with the right element height.
            this._refreshStickyElement(null);
        }
    }
    disableSticky() {
        this.config$.nextKeyValue('disabled', true, { skipCoercion: true });
    }
    enableSticky() {
        this.config$.nextKeyValue('disabled', false, { skipCoercion: true });
    }
    getSticky() {
        if (!this._sticky) {
            this._sticky = this._computeSticky();
        }
        return this._sticky;
    }
    refresh(computation) {
        this._refresh$.next(computation);
    }
    _computeSticky() {
        // IMPORTANT: refresh sticky element to its normal state is required to compute repainted element height.
        this._refreshStickyElement(null);
        this._refreshStickyElement('normal');
        const config = this.config$.getValue();
        // element and spot rects which reflects last screen repaint
        const elementRect = getElementAbsoluteRect(this.elementRef.nativeElement);
        const spotRect = config.spot ? getElementAbsoluteRect(config.spot) : null;
        if (config.height) {
            elementRect.height = config.height;
        }
        if (spotRect && config.spotHeight) {
            spotRect.height = config.spotHeight;
        }
        return {
            disabled: config.disabled,
            boundary: this.boundary ? this.boundary.getBoundary() : null,
            direction: config.direction,
            height: elementRect.height,
            position: config.position,
            top: elementRect.top,
            spot: spotRect,
        };
    }
    /**
     * Create sticky monitoring observable.
     */
    _createMonitoringObservable() {
        return merge(this.config$, fromImageEvents(this.elementRef.nativeElement), ...(this.config.spot ? [fromImageEvents(this.config.spot)] : [])).pipe(debounceTime(0, animationFrameScheduler), map(() => false), takeUntil(this._destroyed$));
    }
    /**
     * Destroy sticky monitoring subscription.
     */
    _destroyMonitoring() {
        if (this._monitoring) {
            this._monitoring.unsubscribe();
            this._monitoring = null;
        }
    }
    /**
     * Returns styles of the given state.
     *
     * `computation` is required when `state` is `"sticked"` or `"stucked"`.
     *
     * @param state Sticky state
     * @param computation Sticky state computation
     * @returns Styles of the sticky state
     */
    _getStickyElementStyle(state, computation) {
        const win = this._win;
        if (!win || !state) {
            return null;
        }
        const ghost = this.config.spacer || this._spacerGenerated;
        const ghostParent = ghost.offsetParent;
        const ghostParentIsRootElement = ghostParent === win.document.body || ghostParent === win.document.documentElement;
        // when state is normal (computation isn't needed)
        if (state === 'normal') {
            const ghostRelativeRect = getElementRelativeRect(win, ghost);
            const ghostStyle = win.getComputedStyle(ghost);
            const ghostBorderBox = ghostStyle.boxSizing === 'border-box';
            let elementWidth = ghostRelativeRect.width;
            if (!ghostBorderBox) {
                elementWidth +=
                    -((parseFloat(ghostStyle.borderLeft) || 0) + (parseFloat(ghostStyle.borderRight) || 0))
                        - ((parseFloat(ghostStyle.paddingLeft) || 0) + (parseFloat(ghostStyle.paddingRight) || 0));
            }
            let elementTop = ghostRelativeRect.top;
            let elementLeft = ghostRelativeRect.left;
            if (ghostParentIsRootElement) {
                if (this.container !== this.rootContainer) {
                    const ghostRect = getElementAbsoluteRect(ghost);
                    const viewportTop = this.container.getViewportTop();
                    elementTop = ghostRect.top - viewportTop;
                }
                elementTop += win.document.documentElement.offsetTop;
                elementLeft += win.document.documentElement.offsetLeft;
            }
            const styles = {
                position: 'absolute',
                width: `${elementWidth}px`,
                top: `${elementTop}px`,
                right: '',
                bottom: '',
                left: `${elementLeft}px`,
                float: '',
                margin: '0px',
            };
            return styles;
        }
        const { container, stickyComputed, viewportHeight } = computation.snap;
        // when state is sticked
        if (state === 'sticked') {
            const positionBottom = stickyComputed.positionBottom;
            let elementTop;
            let elementLeft;
            if (this.container !== this.rootContainer) {
                const ghostRelativeRect = getElementRelativeRect(win, ghost);
                elementLeft = ghostRelativeRect.left;
                if (ghostParentIsRootElement) {
                    elementTop = container.top;
                    elementTop += win.document.documentElement.offsetTop;
                    elementLeft += win.document.documentElement.offsetLeft;
                }
                else {
                    const ghostParentRect = getElementAbsoluteRect(ghostParent);
                    elementTop = computation.viewportTop - ghostParentRect.top;
                }
                if (positionBottom) {
                    elementTop += viewportHeight - stickyComputed.height - computation.offsetSticked - computation.offsetStucked;
                }
                else {
                    elementTop += computation.offsetSticked + computation.offsetStucked;
                }
                return {
                    position: 'absolute',
                    top: `${elementTop}px`,
                    bottom: '',
                    left: `${elementLeft}px`,
                };
            }
            else {
                const ghostRect = getElementAbsoluteRect(ghost);
                elementTop = computation.offsetSticked + computation.offsetStucked;
                elementLeft = ghostRect.left + win.document.documentElement.offsetLeft;
                return {
                    position: 'fixed',
                    top: !positionBottom ? `${elementTop}px` : '',
                    bottom: positionBottom ? `${elementTop}px` : '',
                    left: `${elementLeft}px`,
                };
            }
        }
        // when state is stucked
        if (state === 'stucked') {
            const ghostRect = getElementAbsoluteRect(ghost);
            let elementTop;
            let elementLeft;
            elementTop = getStuckedPositionTop(computation);
            elementLeft = ghostRect.left;
            if (ghostParentIsRootElement) {
                if (this.container !== this.rootContainer) {
                    // we can't use computation.viewportTop because it's absolute viewport top
                    const relativeViewportTop = this.container.getViewportTop();
                    elementTop -= relativeViewportTop;
                }
                elementTop += win.document.documentElement.offsetTop;
                elementLeft += win.document.documentElement.offsetLeft;
            }
            else {
                const ghostParentRect = getElementAbsoluteRect(ghostParent);
                elementTop -= ghostParentRect.top;
                elementLeft -= ghostParentRect.left;
            }
            return {
                position: 'absolute',
                top: `${elementTop}px`,
                bottom: '',
                left: `${elementLeft}px`,
            };
        }
        // throw new Error(`Invalid state: ${state}`);
        return null;
    }
    /**
     * Returns sticky ghost style.
     *
     * @returns Styles of the sticky ghost
     */
    _getStickyGhostStyle() {
        const stickySpacer = this.config.spacer || this._spacerGenerated;
        if (!this._win || !stickySpacer) {
            return null;
        }
        const element = this.elementRef.nativeElement;
        const elementStyle = this._win.getComputedStyle(element);
        const elementnBorderBox = elementStyle.boxSizing === 'border-box';
        let ghostHeight = element.offsetHeight;
        // subtract borders and paddings when element isn't border-boxed
        if (!elementnBorderBox) {
            ghostHeight +=
                // subtract vertical borders
                -(parseFloat(elementStyle.borderTopWidth) || 0)
                    - (parseFloat(elementStyle.borderBottomWidth) || 0)
                    // subtract vertical paddings
                    - (parseFloat(elementStyle.paddingTop) || 0)
                    - (parseFloat(elementStyle.paddingBottom) || 0);
        }
        const styles = {
            // width: element.style.width || elementStyle.width,
            width: elementStyle.width,
            maxWidth: elementStyle.maxWidth,
            minWidth: elementStyle.minWidth,
            height: `${ghostHeight}px`,
            maxHeight: `${ghostHeight}px`,
            minHeight: `${ghostHeight}px`,
            position: elementStyle.position,
            boxSizing: elementStyle.boxSizing,
            cssFloat: elementStyle.cssFloat,
            top: elementStyle.top,
            right: elementStyle.right,
            bottom: elementStyle.bottom,
            left: elementStyle.left,
            borderTop: elementStyle.borderTop,
            borderBottom: elementStyle.borderBottom,
            borderLeft: elementStyle.borderLeft,
            borderRight: elementStyle.borderRight,
            // borderColor: 'transparent',
            marginTop: elementStyle.marginTop,
            marginBottom: elementStyle.marginBottom,
            marginLeft: elementStyle.marginLeft,
            marginRight: elementStyle.marginRight,
            paddingTop: elementStyle.paddingTop,
            paddingBottom: elementStyle.paddingBottom,
            paddingLeft: elementStyle.paddingLeft,
            paddingRight: elementStyle.paddingRight,
        };
        // if (this.config.orbit) {
        //   styles.position = 'absolute';
        //   // styles.width = element.style.width || elementStyle.width;
        //   styles.width = elementStyle.width;
        // }
        return styles;
    }
    /**
     * Hides sticky ghost.
     */
    _hideStickyGhost() {
        const ghost = this.config.spacer || this._spacerGenerated;
        if (!ghost) {
            return;
        }
        this.renderer.setStyle(ghost, 'display', 'none');
    }
    /**
     * Init sticky monitoring.
     */
    _initMonitoring() {
        if (!this._win || this._monitoring) {
            return;
        }
        this.ngZone.runOutsideAngular(() => {
            const handleRefreshSubscription = this._refresh$.pipe(distinctUntilChanged(), takeUntil(this._destroyed$)).subscribe(computation => {
                this._refreshSticky(computation);
            });
            const triggerUpdateSubscription = this._createMonitoringObservable().pipe(takeUntil(this._destroyed$)).subscribe(fastUpdate => {
                this.update(fastUpdate);
            });
            this._monitoring = new Subscription();
            this._monitoring.add(handleRefreshSubscription);
            this._monitoring.add(triggerUpdateSubscription);
        });
    }
    /**
     * Inserts sticky ghost generated.
     */
    _insertStickyGhostGenerated() {
        if (this._spacerGenerated) {
            return;
        }
        const element = this.elementRef.nativeElement;
        const ghost = this.renderer.createElement(element.tagName);
        this.renderer.addClass(ghost, 'ngx-sticky-spacer');
        // this.renderer.setStyle(ghost, 'borderStyle', 'solid');
        // this.renderer.setStyle(ghost, 'borderColor', 'transparent');
        this.renderer.insertBefore(element.parentElement, ghost, element);
        this._spacerGenerated = ghost;
    }
    /**
     * Log nested sticky error and returns `true` when sticky is in another sticky.
     *
     * @returns `true` when sticky is in another sticky
     */
    _preventNestedStickyError() {
        if (!this.stickyParent) {
            return false;
        }
        const nestedStickyError = new Error('Nested sticky is not support. Sticky will not work.');
        if (isDevMode()) {
            console.error(nestedStickyError);
        }
        else {
            console.warn(nestedStickyError);
        }
        return true;
    }
    /**
     * Refresh sticky with given computation.
     *
     * @param computation Sticky state computation
     */
    _refreshSticky(computation) {
        if (
        // refresh sticky when state has changed
        computation.state !== this._stickyElementState
            // or when sticky is in container (other than window)
            || this.container !== this.rootContainer) {
            this._refreshStickyElement(computation.state, computation);
        }
        this._stickyComputation = computation;
        this.stickyComputation.next(computation);
        if (computation.state === this._stickyState) {
            return;
        }
        this.ngZone.run(() => {
            this._stickyState = computation.state;
            this.stickyState.next(computation.state);
            // this.changeDetectorRef.detectChanges();
        });
    }
    /**
     * Refreshs sticky element style.
     *
     * @param state Sticky state
     * @param computation Sticky state computation when state is sticked or stucked
     */
    _refreshStickyElement(state, computation) {
        if (!this._win) {
            return;
        }
        // hide ghost and refresh original style when state is null
        if (!state) {
            this._stickyElementState = null;
            this._hideStickyGhost();
            this._restoreStickyElementStyle();
            return;
        }
        this._stickyElementState = state;
        this._saveStickyElementStyle();
        this._showStickyGhost();
        const elementStyle = this._getStickyElementStyle(state, computation);
        setElementStyles(this.renderer, this.elementRef.nativeElement, elementStyle);
    }
    /**
     * Refreshs sticky ghost.
     */
    _refreshStickyGhost() {
        const ghost = this.config.spacer || this._spacerGenerated;
        const ghostStyle = this._getStickyGhostStyle();
        setElementStyles(this.renderer, ghost, ghostStyle);
    }
    /**
     * Restore original styles of the sticky.
     */
    _restoreStickyElementStyle() {
        setElementStyles(this.renderer, this.elementRef.nativeElement, this._elementOriginStyle);
        this._elementOriginStyle = null;
    }
    /**
     * Saves origin styles of the sticky.
     */
    _saveStickyElementStyle() {
        if (!this._elementOriginStyle) {
            this._elementOriginStyle = {
                position: this.elementRef.nativeElement.style.position,
                width: this.elementRef.nativeElement.style.width,
                top: this.elementRef.nativeElement.style.top,
                right: this.elementRef.nativeElement.style.right,
                bottom: this.elementRef.nativeElement.style.bottom,
                left: this.elementRef.nativeElement.style.left,
                cssFloat: this.elementRef.nativeElement.style.cssFloat,
                margin: this.elementRef.nativeElement.style.margin,
                marginTop: this.elementRef.nativeElement.style.marginTop,
                marginRight: this.elementRef.nativeElement.style.marginRight,
                marginBottom: this.elementRef.nativeElement.style.marginBottom,
                marginLeft: this.elementRef.nativeElement.style.marginLeft,
            };
        }
    }
    /**
     * Shows sticky ghost.
     */
    _showStickyGhost() {
        if (!this.config.spacer && !this._spacerGenerated) {
            this._insertStickyGhostGenerated();
            this._refreshStickyGhost();
            return;
        }
        else if (this.config.spacer && this._spacerGenerated) {
            this._spacerGenerated.remove();
            this._spacerGenerated = null;
        }
        const ghost = this.config.spacer || this._spacerGenerated;
        if (ghost.style.display === 'none') {
            this.renderer.setStyle(ghost, 'display', 'block');
            this._refreshStickyGhost();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyDirective, deps: [{ token: NgxStickyRootContainerController }, { token: forwardRef(() => NgxStickyContainerDirective), optional: true }, { token: forwardRef(() => NgxStickyBoundaryDirective), optional: true }, { token: forwardRef(() => NgxStickyDirective), optional: true, skipSelf: true }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.NgZone }, { token: NGX_STICKY_WINDOW }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.0", type: NgxStickyDirective, selector: "[ngxSticky], [ngx-sticky], ngx-sticky", inputs: { stickyClasses: "stickyClasses", stickyDirection: "stickyDirection", stickyDisabled: "stickyDisabled", stickyHeight: "stickyHeight", stickyPosition: "stickyPosition", stickySpacer: "stickySpacer", stickySpot: "stickySpot", stickySpotHeight: "stickySpotHeight" }, outputs: { stickyComputation: "stickyComputation", stickyState: "stickyState" }, host: { properties: { "attr.data-sticky-state": "this.attrDataStickyState", "class.ngx-sticky": "this.cssClassSticky", "class.ngx-sticky--normal": "this.cssClassStickyNormal", "class.ngx-sticky--sticked": "this.cssClassStickySticked", "class.ngx-sticky--stucked": "this.cssClassStickyStucked", "class.ngx-sticky--disabled": "this.cssClassStickyDisabled" } }, exportAs: ["ngxSticky"], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxSticky], [ngx-sticky], ngx-sticky',
                    exportAs: 'ngxSticky',
                }]
        }], ctorParameters: () => [{ type: NgxStickyRootContainerController }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [forwardRef(() => NgxStickyContainerDirective)]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [forwardRef(() => NgxStickyBoundaryDirective)]
                }] }, { type: undefined, decorators: [{
                    type: SkipSelf
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [forwardRef(() => NgxStickyDirective)]
                }] }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.NgZone }, { type: Window, decorators: [{
                    type: Inject,
                    args: [NGX_STICKY_WINDOW]
                }] }], propDecorators: { stickyClasses: [{
                type: Input
            }], stickyDirection: [{
                type: Input
            }], stickyDisabled: [{
                type: Input
            }], stickyHeight: [{
                type: Input
            }], stickyPosition: [{
                type: Input
            }], stickySpacer: [{
                type: Input
            }], stickySpot: [{
                type: Input
            }], stickySpotHeight: [{
                type: Input
            }], stickyComputation: [{
                type: Output
            }], stickyState: [{
                type: Output
            }], attrDataStickyState: [{
                type: HostBinding,
                args: ['attr.data-sticky-state']
            }], cssClassSticky: [{
                type: HostBinding,
                args: ['class.ngx-sticky']
            }], cssClassStickyNormal: [{
                type: HostBinding,
                args: ['class.ngx-sticky--normal']
            }], cssClassStickySticked: [{
                type: HostBinding,
                args: ['class.ngx-sticky--sticked']
            }], cssClassStickyStucked: [{
                type: HostBinding,
                args: ['class.ngx-sticky--stucked']
            }], cssClassStickyDisabled: [{
                type: HostBinding,
                args: ['class.ngx-sticky--disabled']
            }] } });

/**
 * Adds sticky directives and providers.
 *
 * Managing sticky elements is one of the hardest parts of building web applications.
 *
 * The NgxStickyModule allows to manage sticky elements in the best way.
 *
 * @example
 * NgxStickyModule can be imported multiple times: once per lazily-loaded bundle.
 *
 * ```
 * @NgModule({
 *   imports: [ NgxStickyModule ]
 * })
 * class MyNgModule {}
 * ```
 */
class NgxStickyModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyModule, declarations: [NgxInViewportDirective,
            NgxStickyBoundaryDirective,
            NgxStickyContainerDirective,
            NgxStickyDirective,
            NgxStickySpotDirective], exports: [NgxInViewportDirective,
            NgxStickyBoundaryDirective,
            NgxStickyContainerDirective,
            NgxStickyDirective,
            NgxStickySpotDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.0", ngImport: i0, type: NgxStickyModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        NgxInViewportDirective,
                        NgxStickyBoundaryDirective,
                        NgxStickyContainerDirective,
                        NgxStickyDirective,
                        NgxStickySpotDirective,
                    ],
                    exports: [
                        NgxInViewportDirective,
                        NgxStickyBoundaryDirective,
                        NgxStickyContainerDirective,
                        NgxStickyDirective,
                        NgxStickySpotDirective,
                    ],
                }]
        }] });

/*
 * Public API Surface of @enten/ngx-sticky
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ConfigSubject, InputSubject, NGX_BASE_INTERSECTION_CONFIG_SCHEMA, NGX_STICKY_BASE_BOUNDARY_CONFIG_SCHEMA, NGX_STICKY_BASE_CONFIG_SCHEMA, NGX_STICKY_BASE_CONTAINER_CONFIG_SCHEMA, NGX_STICKY_ENGINE_INTERCEPTION_STATE_MAP, NGX_STICKY_WINDOW, NgxInViewportDirective, NgxStickyBaseBoundaryController, NgxStickyBaseContainerController, NgxStickyBaseContainerDirective, NgxStickyBaseController, NgxStickyBoundaryDirective, NgxStickyContainerDirective, NgxStickyDirective, NgxStickyEngine, NgxStickyModule, NgxStickyRootContainerController, NgxStickySpotDirective, _isNumberValue, addEntry, coerceBooleanProperty, coerceIntersectionThresholds, coerceNumberProperty, coerceStickyDirection, coerceStickyPosition, compareStickiesComputed, deleteEntry, fromImageEvents, fromMediaQuery, getCrossedThreshold, getDocumentHeightFactory, getDocumentWidthFactory, getElementAbsoluteRect, getElementRelativeRect, getStuckedPositionTop, getWindowViewportHeight, getWindowViewportLeft, getWindowViewportTop, isElementScrollableY, isEmptyStyleRuleNeeded, isSmoothScrollSupported, isStickyDirectionDown, isStickyPositionBottom, scrollToFactory, setElementStyles };
